import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import Logo from "@/components/Logo";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Copy, Check, X, AlertTriangle, ChevronDown, HelpCircle, MessageCircle, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import ClipVideo from "@/components/ClipVideo";
import GuiaConexionOlimpo, { MensajeParaCopiar } from "@/components/GuiaConexionOlimpo";
import Chat from "@/components/Chat";
import CountUp from "@/components/CountUp";
import LoaderOlimpo from "@/components/LoaderOlimpo";
import Markdown from "@/components/Markdown";
import { useAuth } from "@/context/AuthContext";
import { useMomentos } from "@/context/MomentosContext";
import track from "@/lib/track";
import PuertasScreen from "@/components/PuertasScreen";
import GenerandoImplementacion from "@/components/GenerandoImplementacion";
import useRefetchOnFocus from "@/hooks/useRefetchOnFocus";
import { enlaceChatGPTWork } from "@/lib/chatgptLink";

const SUG_RE = /\[\[S\]\]([\s\S]*?)\[\[\/S\]\]/g;
const stripMarkers = (md) => (md || "").replace(SUG_RE, "$1");
const contarSugerencias = (md) => ((md || "").match(SUG_RE) || []).length;

function parseSecciones(md) {
  if (!md) return [];
  return md.split(/\n(?=# )/).map((b) => {
    const lines = b.trim().split("\n");
    const titulo = lines[0].replace(/^#\s*/, "");
    const contenido = lines.slice(1).join("\n").trim();
    return { titulo, contenido };
  }).filter((s) => s.titulo);
}

function DocContenido({ contenido, sugDesde, onBorrar, readOnly = false }) {
  const partes = [];
  let last = 0, idx = 0, sugCount = 0;
  const re = new RegExp(SUG_RE.source, "g");
  let m;
  while ((m = re.exec(contenido)) !== null) {
    if (m.index > last) partes.push({ tipo: "texto", val: contenido.slice(last, m.index), key: idx++ });
    partes.push({ tipo: "sug", val: m[1], gi: sugDesde + sugCount, key: idx++ });
    sugCount++;
    last = m.index + m[0].length;
  }
  if (last < contenido.length) partes.push({ tipo: "texto", val: contenido.slice(last), key: idx++ });
  return (
    <div className="text-crema text-sm leading-relaxed">
      {partes.map((p) =>
        p.tipo === "texto" ? (
          <Markdown key={p.key}>{p.val}</Markdown>
        ) : (
          <div key={p.key} className="rounded-md px-2 py-1.5 my-1.5"
            style={{ background: "rgba(201,168,76,0.05)", border: "1px solid rgba(201,168,76,0.22)" }}
            data-testid={`sugerido-${p.gi}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="mono-label" style={{ fontSize: "9px", letterSpacing: "0.12em", color: "rgba(201,168,76,0.7)" }}>SUGERIDO</span>
              {!readOnly && (
                <button onClick={() => onBorrar(p.gi)} className="text-gris hover:text-rojo transition-colors" title="Borrar esta sugerencia" data-testid={`sugerido-borrar-${p.gi}`}>
                  <X size={13} />
                </button>
              )}
            </div>
            <Markdown>{p.val}</Markdown>
          </div>
        )
      )}
    </div>
  );
}

// Panel del pedido para pegar en Claude — PROMPT 6: separa lo que se COPIA (pedido, 2da persona) de
// lo que se LEE (guía), y muestra el ida y vuelta completo (copiar → pegar → traer la respuesta) en
// la misma pantalla, con una salida para cuando la IA pregunta en vez de arrancar.
const norm = (t) => (t || "").toLowerCase();
function limpiarItem(t) {
  return stripMarkers(t || "").replace(/^\s*(?:[-*·•]|\d+[.)])\s*/, "").trim();
}
function parseItems(md) {
  return stripMarkers(md || "").split("\n").map((l) => l.trim()).filter(Boolean)
    .filter((l) => /^([-*·•]|\d+[.)])\s+/.test(l)).map(limpiarItem).filter(Boolean);
}
function primeraFrase(md) {
  const txt = stripMarkers(md || "").replace(/[#*_`>]/g, "").replace(/\s+/g, " ").trim();
  const m = txt.match(/^(.+?[.!?])(?:\s|$)/);
  let r = m ? m[1] : txt;
  if (r.length > 220) r = r.slice(0, 200).trim() + "…";
  return r;
}
const FORMA_BADGE = { A: "Trabajo asistido · Claude", B: "Con conectores · Claude", C: "Tarea programada · Claude", D: "Carpeta de tu computadora · Claude", E: "Base de consulta · Claude", F: "Control de la computadora · ChatGPT", G: "Cambio de práctica" };

// Tarjeta de un paso de acción (copiar / pegar / traer respuesta). Solo presentación.
function StepCard({ n, estado, titulo, children, testid }) {
  const activo = estado === "activo";
  const hecho = estado === "hecho";
  return (
    <div data-testid={testid} data-estado={estado}
      className="rounded-xl p-4 flex flex-col transition-all duration-300"
      style={{
        border: activo ? "1px solid rgba(201,168,76,0.55)" : "1px solid #262626",
        background: activo ? "rgba(201,168,76,0.06)" : "rgba(255,255,255,0.015)",
        opacity: estado === "pendiente" ? 0.45 : 1,
        boxShadow: activo ? "0 0 22px rgba(201,168,76,0.14)" : "none",
      }}>
      <div className="flex items-center gap-2.5 mb-2">
        <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold"
          style={hecho
            ? { background: "rgba(111,191,169,0.16)", border: "1px solid #6FBFA9", color: "#6FBFA9" }
            : activo
              ? { background: "rgba(201,168,76,0.16)", border: "1px solid #C9A84C", color: "#C9A84C" }
              : { border: "1px solid #3C3C3C", color: "#888" }}>
          {hecho ? <Check size={15} /> : n}
        </span>
        <span className="text-crema font-medium" style={{ fontSize: "15px" }}>{titulo}</span>
      </div>
      {children}
    </div>
  );
}

// Acordeón cerrado por defecto, con contador opcional en el título.
function Acordeon({ titulo, contador, abierto, onToggle, children, testid }) {
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: "1px solid #262626", background: "rgba(255,255,255,0.015)" }} data-testid={testid}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-white/[0.02]" data-testid={testid + "-toggle"}>
        <span className="text-crema flex-1" style={{ fontSize: "15px" }}>
          {titulo}
          {contador ? <span className="text-gris ml-2" style={{ fontSize: "13px" }}>{"\u00A0·\u00A0" + contador}</span> : null}
        </span>
        <ChevronDown size={18} className={"text-gris transition-transform shrink-0 " + (abierto ? "rotate-180" : "")} />
      </button>
      <AnimatePresence initial={false}>
        {abierto && (
          <motion.div key="c" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
            <div className="px-4 pb-4 pt-1" style={{ maxWidth: "720px" }}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PedidoAnterior({ texto }) {
  if (!texto) return null;
  return <details className="rounded-xl border border-dorado/30 p-4 text-left" data-testid="pedido-anterior">
    <summary className="text-crema cursor-pointer" data-testid="gen-ver-anterior">Ver el pedido anterior para revisarlo</summary>
    <p className="text-gris text-sm my-3">Esta es la versión anterior guardada. La actualización todavía no está lista.</p>
    <div className="text-crema text-sm max-h-[52vh] overflow-y-auto break-words" data-testid="pedido-anterior-texto"><Markdown>{texto}</Markdown></div>
  </details>;
}

function ConsultaOlimpo({ id, nombre, requiereControl, onAviso, andando }) {
  const [copiado, setCopiado] = useState(false);
  const consulta = `Consultá @Olimpo y leé las instrucciones vigentes de «${nombre}» (ID ${id}). Confirmá el nombre y pedime los datos que necesitás. Esperá mis datos antes de empezar. Seguí las reglas y los límites del proceso; mostrame el resultado para revisarlo antes de registrar una corrida.${requiereControl ? " Cuando te dé los datos, usá @computer para trabajar en mi computadora según las instrucciones. Pedime acceso si falta y esperá mi confirmación antes de una acción que lo requiera." : ""}`;
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(consulta);
      setCopiado(true);
      toast.success("Mensaje copiado. Pegalo en ChatGPT si no apareció automáticamente.");
    } catch { toast.error("No pude copiarlo. Seleccioná el mensaje de abajo y copialo."); }
  };
  return (
    <div className="rounded-xl p-5 mb-6 space-y-4 border border-dorado/40" data-testid="consulta-olimpo">
      <h3 className="text-crema text-xl">Usá este proceso en ChatGPT</h3>
      <p className="text-crema text-base leading-relaxed">El botón abre Work con el mensaje listo. Tocá <b>Enviar</b>: ChatGPT buscará tus instrucciones en Olimpo y te pedirá los datos para empezar.</p>
      <a href={enlaceChatGPTWork(consulta)} target="_blank" rel="noreferrer" className="btn-dorado inline-flex items-center min-h-[44px]" data-testid="abrir-chatgpt">ABRIR MI PROCESO EN CHATGPT</a>
      {requiereControl && <div className="space-y-2 text-crema text-base">
        <p>Para trabajar sobre tu computadora, usá la aplicación de escritorio de ChatGPT con la misma cuenta. Continuá esta misma conversación desde Recientes.</p>
        <button className="block mono-label text-gris underline min-h-[44px]" onClick={onAviso} data-testid="aviso-cu-releer">Cómo revisar y detener la primera prueba</button>
      </div>}
      <p className="text-crema text-base border-l-2 border-dorado pl-3" data-testid="consulta-cuando-volver">{andando ? "Para usarlo otra vez, pasale los datos del nuevo caso en ese chat y revisá el resultado." : <>Cuando ChatGPT nombre «{nombre}» y te pida los datos, podés continuar acá con la primera prueba.</>}</p>
      <details className="text-crema text-base" data-testid="consulta-alternativa">
        <summary className="cursor-pointer py-2 min-h-[44px]">El mensaje no apareció o no encuentra Olimpo</summary>
        <div className="space-y-3 py-2">
          <p>En ChatGPT elegí Work, escribí @Olimpo y seleccionalo. Pegá este mensaje y tocá Enviar. Si todavía no está instalado, volvé a «Repasar la preparación» para conectarlo.</p>
          <button onClick={copiar} className="btn-fantasma flex items-center gap-2 min-h-[44px]" data-testid="copiar-consulta-olimpo"><Copy size={15} /> {copiado ? "MENSAJE COPIADO" : "COPIAR MENSAJE"}</button>
          <p className="select-text leading-relaxed break-words text-sm" data-testid="consulta-olimpo-texto">{consulta}</p>
        </div>
      </details>
    </div>
  );
}

// El instructivo: la tarea principal de la pantalla. Hero corto + 3 pasos de acción +
// acordeones (pedido completo / lo que sigue siendo tuyo / cómo saber si funcionó).
// COPIAR EL PEDIDO copia el pedido COMPLETO (con el freno inmutable primero) al portapapeles.
function DocumentoPanel({ impl, setImpl, id, nombre }) {
  const [copiado, setCopiado] = useState(false);
  const [copiadoPedido, setCopiadoPedido] = useState(false);
  const [pasoCopiado, setPasoCopiado] = useState(false);
  const [pasoRespondio, setPasoRespondio] = useState(false);
  const [respuesta, setRespuesta] = useState("");
  const [feedback, setFeedback] = useState("");
  const [cargandoResp, setCargandoResp] = useState(false);
  const [pregunta, setPregunta] = useState("");
  const [respPregunta, setRespPregunta] = useState("");
  const [cargandoPreg, setCargandoPreg] = useState(false);
  const [copiadoPreg, setCopiadoPreg] = useState(false);
  const [abiertos, setAbiertos] = useState({});
  const toggle = (k) => setAbiertos((a) => ({ ...a, [k]: !a[k] }));
  const { pedir, listo, vistos } = useMomentos();

  // Aviso PARA LA PERSONA en formas de control de la computadora (Forma F): se muestra una vez por
  // implementación, antes de copiar. Después queda accesible por un link chiquito para releerlo.
  const esControlCompu = impl?.forma === "F" && impl?.requiere_control === true;
  const avisoKey = `olimpo_cu_aviso_${id}${impl?.conector_ui ? "_conector_v1" : ""}`;
  const avisoCUData = impl?.aviso_cu;
  const [avisoCU, setAvisoCU] = useState(false);
  useEffect(() => {
    if (!esControlCompu || avisoCU) return;
    if (localStorage.getItem(avisoKey)) return;
    if (!listo) return;  // esperar a que carguen los momentos (para no saltear m7 por una carrera)
    // Primera Forma F de la cuenta: antes del modal va el momento "un empleado nuevo". Las siguientes, directo al modal.
    if (!vistos.includes("m7")) {
      pedir("m7", {}, () => setAvisoCU(true));
    } else {
      setAvisoCU(true);
    }
  }, [esControlCompu, avisoKey, listo, vistos, avisoCU, pedir]);

  const ia = impl?.forma === "F" ? "ChatGPT" : "Claude";
  const pedido = impl?.pedido ?? impl?.documento ?? "";
  const guia = impl?.guia ?? "";
  const secciones = parseSecciones(guia);
  const logroSec = secciones.find((s) => norm(s.titulo).includes("vas a lograr"));
  const heroFrase = logroSec ? primeraFrase(logroSec.contenido) : "";
  const restantes = secciones.filter((s) => !norm(s.titulo).includes("vas a lograr"));
  const badge = impl.conector_ui && !impl.requiere_control ? "ChatGPT · Conector Olimpo" : FORMA_BADGE[impl.forma] || "Implementar";

  const copiarPedidoFull = async (marcarBoton) => {
    await navigator.clipboard.writeText(stripMarkers(pedido));
    track("pedido_copiado", { proceso_id: id });
    setPasoCopiado(true);
    if (marcarBoton) { setCopiado(true); setTimeout(() => setCopiado(false), 2200); }
    else { setCopiadoPedido(true); setTimeout(() => setCopiadoPedido(false), 2200); }
    toast.success(`Copiado. Pegalo en ${ia}.`);
  };
  const cerrarAviso = (copiar) => {
    try { localStorage.setItem(avisoKey, "1"); } catch (e) { /* noop */ }
    setAvisoCU(false);
    if (copiar) copiarPedidoFull(true);
  };
  const borrarSugerencia = async (gi) => {
    let i = 0;
    const re = new RegExp(SUG_RE.source, "g");
    const nuevo = pedido.replace(re, (mm) => { const keep = i !== gi; i++; return keep ? mm : ""; }).replace(/[ \t]*\n{3,}/g, "\n\n");
    setImpl({ ...impl, pedido: nuevo });
    try { await api.patch(`/implementacion/${id}`, { pedido: nuevo }); toast.success("Listo, la sacamos del pedido."); }
    catch (e) { toast.error("No se pudo borrar. Probá de nuevo."); }
  };
  const enviarRespuesta = async () => {
    if (!respuesta.trim() || cargandoResp) return;
    setCargandoResp(true); setFeedback("");
    try { const { data } = await api.post(`/implementacion/${id}/ayuda-claude`, { modo: "respuesta", texto: respuesta }); setFeedback(data.texto || ""); }
    catch (e) { toast.error("No se pudo. Probá de nuevo."); }
    setCargandoResp(false);
  };
  const enviarPregunta = async () => {
    if (!pregunta.trim() || cargandoPreg) return;
    setCargandoPreg(true); setRespPregunta("");
    try { const { data } = await api.post(`/implementacion/${id}/ayuda-claude`, { modo: "pregunta", texto: pregunta }); setRespPregunta(data.texto || ""); }
    catch (e) { toast.error("No se pudo. Probá de nuevo."); }
    setCargandoPreg(false);
  };
  const copiarPreg = async () => {
    await navigator.clipboard.writeText(respPregunta);
    setCopiadoPreg(true);
    toast.success(`Copiado. Pegáselo a ${ia}.`);
    setTimeout(() => setCopiadoPreg(false), 2200);
  };

  const paso1 = pasoCopiado ? "hecho" : "activo";
  const paso2 = !pasoCopiado ? "pendiente" : (pasoRespondio ? "hecho" : "activo");
  const paso3 = pasoRespondio ? "activo" : "pendiente";

  const renderSeccion = (s, idx) => {
    const t = norm(s.titulo);
    if (t.includes("sigue siendo tuyo")) {
      const items = parseItems(s.contenido);
      return (
        <Acordeon key={idx} testid="acc-tuyo" titulo="Lo que sigue siendo tuyo" contador={items.length ? `${items.length} ${items.length === 1 ? "cosa" : "cosas"}` : ""} abierto={!!abiertos["tuyo"]} onToggle={() => toggle("tuyo")}>
          {items.length ? (
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="acc-tuyo-lista">
              {items.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-crema" style={{ fontSize: "15px", lineHeight: 1.6 }}>
                  <Check size={15} className="text-verde shrink-0 mt-1" /><span>{it}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-crema" style={{ fontSize: "15px", lineHeight: 1.6 }}><DocContenido contenido={s.contenido} sugDesde={0} onBorrar={() => {}} readOnly /></div>
          )}
        </Acordeon>
      );
    }
    if (t.includes("prueba")) {
      const items = parseItems(s.contenido);
      return (
        <Acordeon key={idx} testid="acc-prueba" titulo="Cómo saber si funcionó" abierto={!!abiertos["prueba"]} onToggle={() => toggle("prueba")}>
          {items.length ? (
            <ol className="space-y-2.5 list-none" data-testid="acc-prueba-lista">
              {items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-crema" style={{ fontSize: "15px", lineHeight: 1.6 }}>
                  <span className="mono-label text-dorado shrink-0" style={{ fontSize: "12px", marginTop: "3px" }}>{i + 1}</span><span>{it}</span>
                </li>
              ))}
            </ol>
          ) : (
            <div className="text-crema" style={{ fontSize: "15px", lineHeight: 1.6 }}><DocContenido contenido={s.contenido} sugDesde={0} onBorrar={() => {}} readOnly /></div>
          )}
        </Acordeon>
      );
    }
    const titulo = (t.includes("control de la computadora")) ? "Antes de empezar: activá el control de la computadora" : s.titulo;
    return (
      <Acordeon key={idx} testid={"acc-extra-" + idx} titulo={titulo} abierto={!!abiertos["extra" + idx]} onToggle={() => toggle("extra" + idx)}>
        <div className="text-crema" style={{ fontSize: "15px", lineHeight: 1.6 }}><DocContenido contenido={s.contenido} sugDesde={0} onBorrar={() => {}} readOnly /></div>
      </Acordeon>
    );
  };

  return (
    <div data-testid="documento-panel">
      {/* Hero: nombre del proceso + forma + una sola frase de lo que va a lograr */}
      <div className="mb-8" data-testid="impl-hero">
        <span className="inline-block rounded-md px-2.5 py-1 mb-3 font-medium" style={{ background: "#C9A84C", color: "#0A0A0A", fontSize: "12px", letterSpacing: "0.02em" }} data-testid="hero-forma-badge">{badge}</span>
        <h1 className="titulo text-crema mb-3" style={{ fontSize: "clamp(28px,4vw,40px)", lineHeight: 1.1 }} data-testid="hero-nombre">{nombre}</h1>
        {heroFrase && <p className="text-crema" style={{ fontSize: "16px", lineHeight: 1.6, maxWidth: "620px" }} data-testid="hero-frase">{heroFrase}</p>}
      </div>

      {/* Los 3 pasos de acción, en tarjetas horizontales */}
      {impl.conector_ui ? <><PrepVideo video={impl.video_inicio_conector} /><ConsultaOlimpo id={id} nombre={nombre} requiereControl={impl.requiere_control} andando={impl.estado === "andando"} onAviso={() => setAvisoCU(true)} /></> : (
      <div className="grid gap-4 sm:grid-cols-3 mb-6" data-testid="pasos-accion">
        <StepCard n={1} estado={paso1} titulo="Copiá el pedido" testid="paso-accion-1">
          <p className="text-gris mb-3" style={{ fontSize: "13px", lineHeight: 1.6 }}>Llevá todo el pedido a {ia}, sin cambiar nada.</p>
          <button className="btn-dorado w-full flex items-center justify-center gap-2 mt-auto !py-3" onClick={() => copiarPedidoFull(true)} data-testid="copiar-claude">
            {copiado ? <Check size={16} /> : <Copy size={16} />} {copiado ? "Copiado" : "Copiar el pedido"}
          </button>
          {esControlCompu && (
            <button className="mono-label text-gris hover:text-crema mt-2 transition-colors" style={{ fontSize: "11px" }} onClick={() => setAvisoCU(true)} data-testid="aviso-cu-releer">
              ¿Cómo trabaja esta primera vuelta?
            </button>
          )}
        </StepCard>
        <StepCard n={2} estado={paso2} titulo={impl.forma === "F" ? "Abrí Work y pegalo" : "Pegalo y mandalo"} testid="paso-accion-2">
          <p className="text-gris mb-3" style={{ fontSize: "13px", lineHeight: 1.6 }}>
            {impl.forma === "F"
              ? "Abrí ChatGPT, elegí Work y pegá el pedido completo. Ya empieza con @Computer: no hace falta escribir nada más."
              : `Pegalo en ${ia} con Cmd+V (Mac) o Ctrl+V, mandalo y esperá la confirmación.`}
          </p>
          {impl.forma === "F" && (
            <a href="https://chatgpt.com/codex/open-app?app_brand=chatgpt" target="_blank" rel="noreferrer" className="btn-fantasma w-full flex items-center justify-center mt-auto !py-3" data-testid="abrir-chatgpt-manual">
              ABRIR CHATGPT WORK
            </a>
          )}
        </StepCard>
        <StepCard n={3} estado={paso3} titulo={impl.forma === "F" ? "Dale el caso y miralo" : "Traé la respuesta"} testid="paso-accion-3">
          <p className="text-gris mb-3" style={{ fontSize: "13px", lineHeight: 1.6 }}>{impl.forma === "F" ? "Pasale los datos del primer caso, quedate mirando y revisá el resultado cuando termine." : `Cuando ${ia} conteste, traé su respuesta acá y la reviso con vos.`}</p>
          {!pasoRespondio && (
            <button className="btn-fantasma w-full flex items-center justify-center gap-2 mt-auto !py-3" onClick={() => setPasoRespondio(true)} data-testid="abrir-respuesta">
              {impl.forma === "F" ? <Check size={15} /> : <MessageCircle size={15} />} {impl.forma === "F" ? "Ya lo vi trabajar" : "Pegar lo que me respondió"}
            </button>
          )}
        </StepCard>
      </div>
      )}

      {contarSugerencias(pedido) > 0 && (
        <div className="rounded-xl p-3 mb-6 flex items-start gap-2" style={{ border: "1px solid #262626", background: "rgba(255,255,255,0.02)" }} data-testid="sugeridos-aviso">
          <AlertTriangle size={15} className="text-gris shrink-0 mt-0.5" />
          <p className="text-gris" style={{ fontSize: "13px", lineHeight: 1.6 }}>Lo <span style={{ color: "rgba(201,168,76,0.85)" }} className="font-medium">sugerido</span> lo sumamos nosotros. Abrí el pedido completo y borrá con la <X size={11} className="inline" /> lo que no aplique.</p>
        </div>
      )}

      {/* Área de respuesta (paso 3) */}
      {pasoRespondio && impl.forma !== "F" && (
        <div className="rounded-xl p-4 mb-6" style={{ border: "1px solid rgba(201,168,76,0.3)", background: "rgba(201,168,76,0.04)" }} data-testid="respuesta-area">
          <p className="text-crema mb-3" style={{ fontSize: "15px", lineHeight: 1.6 }}>Pegá acá lo que te devolvió {ia}. Lo miro y te digo si quedó bien.</p>
          <textarea className="input-dark min-h-[110px] text-sm" placeholder={`Pegá acá lo que te devolvió ${ia}…`} value={respuesta} onChange={(e) => setRespuesta(e.target.value)} data-testid="respuesta-claude" />
          <button className="btn-dorado mt-2 text-xs !py-2" onClick={enviarRespuesta} disabled={!respuesta.trim() || cargandoResp} data-testid="pegar-respuesta">{cargandoResp ? "MIRANDO…" : "PEGAR LO QUE ME RESPONDIÓ"}</button>
          {feedback && (<div className="card-dark p-4 mt-3 text-crema text-sm leading-relaxed" data-testid="respuesta-feedback"><Markdown>{feedback}</Markdown></div>)}
          <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(60,60,60,0.5)" }} data-testid="claude-pregunto-bloque">
            <p className="text-crema mb-2" style={{ fontSize: "14px", lineHeight: 1.6 }}>¿Te preguntó algo en vez de arrancar? Pegame lo que te dijo y te digo exactamente qué contestarle.</p>
            <textarea className="input-dark min-h-[90px] text-sm" placeholder={`Pegá acá la pregunta que te hizo ${ia}…`} value={pregunta} onChange={(e) => setPregunta(e.target.value)} data-testid="pregunta-claude" />
            <button className="btn-fantasma mt-2 text-xs !py-2" onClick={enviarPregunta} disabled={!pregunta.trim() || cargandoPreg} data-testid="pedir-respuesta-pregunta">{cargandoPreg ? "PENSANDO…" : "DECIME QUÉ CONTESTAR"}</button>
            {respPregunta && (
              <div className="card-dark p-4 mt-3" data-testid="respuesta-para-pegar">
                <div className="mono-label text-gris mb-2">PEGÁLE ESTO A {ia.toUpperCase()}</div>
                <div className="text-crema text-sm leading-relaxed whitespace-pre-wrap mb-3">{respPregunta}</div>
                <button className="btn-fantasma flex items-center gap-2 text-xs" onClick={copiarPreg} data-testid="copiar-respuesta-pregunta">{copiadoPreg ? <Check size={14} /> : <Copy size={14} />} {copiadoPreg ? "COPIADO" : "COPIAR ESTA RESPUESTA"}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Todo lo demás, en acordeones cerrados */}
      <div className="space-y-3" data-testid="acordeones">
        <Acordeon testid="acc-pedido" titulo="Ver el pedido completo" abierto={!!abiertos["pedido"]} onToggle={() => toggle("pedido")}>
          <div className="flex justify-end mb-2">
            <button className="btn-fantasma !py-1.5 !px-3 flex items-center gap-1.5" style={{ fontSize: "11px" }} onClick={() => copiarPedidoFull(false)} data-testid="copiar-pedido-acordeon">
              {copiadoPedido ? <Check size={13} /> : <Copy size={13} />} {copiadoPedido ? "Copiado" : "Copiar"}
            </button>
          </div>
          <div className="rounded-lg p-4 max-h-[52vh] overflow-y-auto" style={{ background: "#0F0F0F", border: "1px solid #262626", fontFamily: "'JetBrains Mono', monospace" }} data-testid="pedido-completo">
            <DocContenido contenido={pedido} sugDesde={0} onBorrar={borrarSugerencia} />
          </div>
        </Acordeon>
        {restantes.map((s, i) => renderSeccion(s, i))}
      </div>

      {createPortal(
        <AnimatePresence>
          {avisoCU && (
            <motion.div
              className="fixed inset-0 z-[200] flex items-center justify-center p-4"
              style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)" }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setAvisoCU(false)}
              data-testid="aviso-cu-overlay"
            >
              <motion.div
                className="rounded-2xl w-full max-w-lg p-7"
                style={{ background: "#141414", border: "1px solid rgba(201,168,76,0.35)" }}
                initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }}
                onClick={(e) => e.stopPropagation()}
                data-testid="aviso-cu-modal"
              >
                <h2 className="titulo text-crema mb-5" style={{ fontSize: "26px", lineHeight: 1.15 }} data-testid="aviso-cu-titulo">{avisoCUData?.titulo || "Antes de pegarlo"}</h2>
                <ul className="space-y-4 mb-7">
                  {(avisoCUData?.items || []).map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-crema" style={{ fontSize: "15px", lineHeight: 1.6 }}>
                      <span className="text-dorado shrink-0 mt-1">·</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <button className="btn-dorado w-full flex items-center justify-center gap-2 !py-3" onClick={() => cerrarAviso(!impl.conector_ui)} data-testid="aviso-cu-entendido">
                  <Check size={16} /> {impl.conector_ui ? "ENTENDIDO" : "ENTENDIDO, COPIAR EL PEDIDO"}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

const FORMA_TAG = { A: "TRABAJO ASISTIDO", B: "CON CONECTORES", C: "TAREA PROGRAMADA", D: "CARPETA DE TU COMPUTADORA", E: "BASE DE CONSULTA", F: "CONTROL DE LA COMPUTADORA", G: "CAMBIO DE PRÁCTICA" };

// Sello de la forma: 1 línea al entrar, después se colapsa a un sello chico (se despliega al tocar).
function SelloForma({ tag, motivo, expandido, setExpandido }) {
  return (
    <div className="inline-flex flex-col items-start gap-2">
      <button
        onClick={() => setExpandido((v) => !v)}
        className="mono-label text-negro px-2.5 py-1 rounded-md hover:brightness-110 transition-all"
        style={{ background: "#C9A84C" }}
        data-testid="forma-sello"
      >
        {tag}
      </button>
      <AnimatePresence>
        {expandido && motivo && (
          <motion.p
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className="text-gris text-sm max-w-xl leading-relaxed"
            data-testid="forma-motivo"
          >
            {motivo}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

// Pantalla de logro al poner un proceso ANDANDO (Fase 3).
function HorasLogro({ target }) {
  const [v, setV] = useState(0);
  useEffect(() => { const t = setTimeout(() => setV(target), 350); return () => clearTimeout(t); }, [target]);
  return <CountUp value={v} duration={1600} />;
}

function LogroScreen({ logro, onSeguir, onMañana }) {
  const s = logro.siguiente;
  const totalDots = Math.min(Math.max(logro.total_focos || logro.andando_count || 1, logro.andando_count || 1), 10);
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[9999] flex flex-col items-center px-6 text-center overflow-y-auto py-16"
      style={{ background: "#0A0A0A" }}
      data-testid="logro-screen"
    >
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }} className="max-w-2xl w-full my-auto">
        {logro.es_primer && (
          <p className="text-dorado text-sm mb-6" data-testid="logro-primero">
            Este es el primero. De acá en adelante ya sabés cómo se hace.
          </p>
        )}
        <div className="mono-label text-gris mb-6" style={{ letterSpacing: "0.25em" }}>PROCESO ANDANDO</div>
        <div className="titulo text-dorado mb-3" style={{ fontSize: "84px", lineHeight: 1.05, paddingTop: "0.08em" }} data-testid="logro-horas">
          <HorasLogro target={logro.horas_proceso} /> <span style={{ fontSize: "36px" }}>horas</span>
        </div>
        <p className="text-crema text-lg mb-2">por mes que acabás de recuperar</p>
        {logro.plata_proceso_mes != null && (
          <p className="text-dorado text-xl mb-3 max-w-lg mx-auto" data-testid="logro-plata">
            y USD {logro.plata_proceso_mes.toLocaleString("es-AR")} por mes que dejás de perder.{logro.plata_proceso_anio != null ? ` Al año son USD ${logro.plata_proceso_anio.toLocaleString("es-AR")}.` : ""}
          </p>
        )}
        {logro.comparacion && <p className="text-gris text-sm mb-10 max-w-lg mx-auto">{logro.comparacion}</p>}

        <div className="flex items-center justify-center gap-2 mb-3" data-testid="logro-puntitos">
          {Array.from({ length: totalDots }).map((_, i) => (
            <span key={i} style={{ color: i < logro.andando_count ? "#C9A84C" : "#3C3C3C", fontSize: "12px" }}>●</span>
          ))}
        </div>
        <p className="text-gris text-sm mb-12" data-testid="logro-acumulado">
          Llevás {logro.andando_count} {logro.andando_count === 1 ? "proceso andando" : "procesos andando"} · {logro.horas_total} horas{logro.plata_total_mes != null ? ` y USD ${logro.plata_total_mes.toLocaleString("es-AR")} por mes` : " por mes en total"}
        </p>

        {s ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 }}
            className="rounded-xl p-6 border max-w-md mx-auto"
            style={{ borderColor: "rgba(201,168,76,0.4)", background: "rgba(201,168,76,0.05)" }}
            data-testid="logro-siguiente"
          >
            <div className="mono-label text-dorado mb-2">LO QUE SIGUE</div>
            <h3 className="titulo text-2xl mb-2">{s.titulo}</h3>
            <p className="text-gris text-sm mb-6">Son {s.horas_mes} horas por mes y lo dejás andando en unos 15 minutos.</p>
            <button className="btn-dorado w-full mb-3" onClick={onSeguir} data-testid="logro-seguir">SEGUIR CON ESE</button>
            <button className="mono-label text-gris hover:text-crema underline transition-colors" onClick={onMañana} data-testid="logro-manana">prefiero seguir mañana</button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}>
            <button className="btn-dorado" onClick={onMañana} data-testid="logro-manana">IR A MI MESA</button>
          </motion.div>
        )}
      </motion.div>
    </motion.div>,
    document.body
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

// Congela qué pasos de preparación forman parte del FLUJO de este proceso (contador + segmentos),
// tomando una foto al abrirlo por primera vez: los pasos PENDIENTES en ese momento. Se guarda por
// proceso+forma. Así, aunque después se marquen como hechos (prep_hecha es a nivel cuenta), el flujo
// mantiene esos pasos y el contador NO se corre al recargar/re-navegar. Los pasos que ya estaban
// hechos ANTES de abrir (preparación de procesos anteriores) NO entran al flujo: van al banner
// colapsado "tu herramienta ya está lista".
function computePrepFlow(allPrep, id, forma) {
  const lista = allPrep || [];
  const snapKey = `olimpo_prep_flow_${id}_${forma || ""}`;
  let flowKeys = null;
  try { const raw = localStorage.getItem(snapKey); flowKeys = raw ? JSON.parse(raw) : null; } catch { flowKeys = null; }
  if (!Array.isArray(flowKeys)) {
    flowKeys = lista.filter((x) => !x.hecho).map((x) => x.prep_key);
    try { localStorage.setItem(snapKey, JSON.stringify(flowKeys)); } catch { /* noop */ }
  }
  // A disconnected account must reappear even if this process cached an empty
  // preparation list while its previous connection was working.
  if (lista.some((x) => x.prep_key === "chatgpt_conector" && !x.hecho)
      && !flowKeys.includes("chatgpt_conector")) {
    flowKeys = [...flowKeys, "chatgpt_conector"];
    try { localStorage.setItem(snapKey, JSON.stringify(flowKeys)); } catch { /* noop */ }
  }
  const enFlujo = new Set(flowKeys);
  const prepFlow = lista.filter((x) => enFlujo.has(x.prep_key));
  const prepColapsados = lista.filter((x) => x.hecho && !enFlujo.has(x.prep_key));
  return { prepFlow, prepColapsados };
}

// Los videos complementan el texto. Una ranura vacía nunca ocupa espacio ni bloquea.
function PrepVideo({ video }) {
  const [abierto, setAbierto] = useState(false);
  if (!video || !(video.tipo === "url" && video.embed_url || video.tipo === "archivo" && video.clip_id)) return null;
  return <div className="mb-5" data-testid="prep-video">
    <button type="button" onClick={() => setAbierto(!abierto)} aria-expanded={abierto} className="btn-fantasma min-h-[44px]" data-testid="prep-video-abrir">{abierto ? "CERRAR VIDEO" : "VER EJEMPLO EN VIDEO (OPCIONAL)"}</button>
    {abierto && <div className="mt-3">
      {video.tipo === "url" ? <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingBottom: "56.25%", background: "#0A0A0A" }}>
        <iframe src={video.embed_url} title="Ejemplo del paso, opcional" allowFullScreen className="absolute top-0 left-0 w-full h-full" />
      </div> : <video src={`${API}/api/clip-video/${video.clip_id}`} playsInline controls preload="metadata" className="w-full rounded-xl" />}
      <p className="text-gris text-sm mt-2">Podés pausar o cerrar el video y seguir con las indicaciones escritas.</p>
    </div>}
  </div>;
}

export default function Implementar() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pedir } = useMomentos();
  const { user, refreshMe } = useAuth();
  const [impl, setImpl] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const avanzandoRef = useRef(false);
  const [avanzando, setAvanzando] = useState(false);
  const [prep, setPrep] = useState([]);
  const [prepFlow, setPrepFlow] = useState([]);        // pasos de prep pendientes al cargar (fijos en la sesión)
  const prepFlowRef = useRef(prepFlow);
  prepFlowRef.current = prepFlow;
  const [prepColapsados, setPrepColapsados] = useState([]); // ya hechos: se muestran colapsados
  const [prepBannerAbierto, setPrepBannerAbierto] = useState(false);
  const [clips, setClips] = useState({});
  const [verClips, setVerClips] = useState(false);
  const [pasoActual, setPasoActual] = useState(0);
  const [pasoTrabajo, setPasoTrabajo] = useState(0); // el paso donde la persona está trabajando (para "volver a donde estaba")
  const [peekPrep, setPeekPrep] = useState(null);    // mirando un paso de preparación ya hecho (no está en el flujo)
  const restauradoRef = useRef(false);               // true una vez restaurada la posición inicial (para persistir sin pisar)
  const [generando, setGenerando] = useState(false);
  const [genDemorado, setGenDemorado] = useState(false);
  const [pedidoAnterior, setPedidoAnterior] = useState("");
  const [paciencia, setPaciencia] = useState(false);
  const genCancel = useRef(null);
  const [nombre, setNombre] = useState("");
  const [cerebro, setCerebro] = useState(null);
  const [chatAbierto, setChatAbierto] = useState(false);
  const [verDoc, setVerDoc] = useState(false);
  const [verPasos, setVerPasos] = useState(false);
  const [inyectar, setInyectar] = useState(null);
  const [nmsCount, setNmsCount] = useState({});
  const [nmsActivo, setNmsActivo] = useState(false);
  const [ayuda, setAyuda] = useState(null);
  const [chequeoCopiado, setChequeoCopiado] = useState(false);
  const [selloExpandido, setSelloExpandido] = useState(true);
  const [cambiandoForma, setCambiandoForma] = useState(false);
  const [verOpciones, setVerOpciones] = useState(false);
  const [logro, setLogro] = useState(null);
  const [puertas, setPuertas] = useState(null);
  const [dir, setDir] = useState(1);
  const [pegado, setPegado] = useState({});  // pasos con criterio: no se avanza hasta pegar el resultado
  const [respuestaPegada, setRespuestaPegada] = useState("");  // caja del paso "Traeme lo que te respondió"
  const [preguntarSaltear, setPreguntarSaltear] = useState(false);  // LISTO sin pegar: preguntamos una vez si quiere saltear
  const [declarandoResultado, setDeclarandoResultado] = useState(false);
  const [cargaError, setCargaError] = useState(false);
  const [faltaMarca, setFaltaMarca] = useState(null); // {pregunta} · proceso complejo sin la marca para distinguir ventanas
  const [marcaTexto, setMarcaTexto] = useState("");
  const [enviandoMarca, setEnviandoMarca] = useState(false);
  const iaNombre = impl?.forma === "F" ? "ChatGPT" : "Claude";

  const aplicarImpl = (im, rebuildFlow = true) => {
    setImpl(im);
    setChecklist(im.checklist || []);
    const allPrep = im.preparacion || [];
    setPrep(allPrep);
    if (rebuildFlow) {
      // El flujo de preparación es una foto estable del proceso (ver computePrepFlow): mantiene los
      // pasos aunque queden hechos, para que el contador/segmentos no se corran al recargar.
      const { prepFlow, prepColapsados } = computePrepFlow(allPrep, id, im.forma);
      setPrepColapsados(prepColapsados);
      prepFlowRef.current = prepFlow;
      setPrepFlow(prepFlow);
    } else {
      // Recarga en caliente (al volver el foco / cuando el asistente guarda): NO rearmamos el
      // flujo. Mantenemos los mismos pasos de la sesión (aunque ya estén hechos) para que el índice
      // del paso no se corra y la pantalla nunca "avance sola".
      const conexionPendiente = allPrep.find((p) => p.prep_key === "chatgpt_conector" && !p.hecho);
      if (conexionPendiente && !prepFlowRef.current.some((p) => p.prep_key === "chatgpt_conector" && !p.hecho)) {
        const actualizado = computePrepFlow(allPrep, id, im.forma);
        prepFlowRef.current = actualizado.prepFlow;
        setPrepFlow(actualizado.prepFlow);
        setPrepColapsados(actualizado.prepColapsados);
        const indice = actualizado.prepFlow.findIndex((p) => !p.hecho);
        setPasoActual(indice);
        setPasoTrabajo(indice);
      } else {
        setPrepFlow((pf) => pf.map((old) => allPrep.find((p) => p.prep_key === old.prep_key) || old));
      }
    }
  };

  useEffect(() => {
    let cancelado = false;
    let implCargada = false;
    setCargaError(false);
    restauradoRef.current = false;
    (async () => {
      try {
      const p = await api.get(`/proceso/${id}`);
      if (cancelado) return;
      setNombre(p.data.proceso.nombre);
      track("implementacion_abierta", { proceso_id: id });
      let data = (await api.get(`/implementacion/${id}`)).data;
      if (cancelado) return;
      setPedidoAnterior(data.implementacion?.pedido || data.implementacion?.documento || "");
      // Proceso complejo sin la marca de ventana: NO generamos a ciegas; pedimos la marca antes.
      if (data.implementacion?.gen_estado === "falta_marca") {
        setFaltaMarca(data.implementacion.pregunta_marca || "");
        setGenerando(false);
        return;
      }
      const forzarRegen = searchParams.get("regenerar") === "1";
      if (forzarRegen || !data.implementacion?.documento || ["generando", "fallo"].includes(data.implementacion?.gen_estado)) {
        setGenerando(true);
        setGenDemorado(false);
        setPaciencia(false);
        if (forzarRegen) {
          // Regeneración EXPLÍCITA: el backend responde async ("generando") y el pedido VIEJO sigue
          // existiendo. No lo mostramos como final: pedimos una vez y esperamos/pollear (sin reenviar
          // force en cada poll) hasta que el nuevo esté listo.
          try {
            const rr = (await api.post(`/implementacion/${id}/generar?regenerar=1`)).data;
            // El endpoint devuelve el estado en el nivel superior (rr.gen_estado). Solo tomamos el
            // resultado directo si YA está "listo" con documento; en cualquier otro estado
            // (generando / falta_marca / fallo) esperamos y sondeamos, sin reenviar force.
            data = (rr.gen_estado === "listo" && rr.implementacion?.documento)
              ? rr : await generarConReintento(false);
          } catch { data = await generarConReintento(false); }
          setSearchParams({}, { replace: true });
        } else if (data.implementacion?.documento) {
          // Abrir un pedido anterior nunca consume una nueva generación. Si sigue en curso,
          // sólo consultamos; si quedó fallido o vencido, ofrecemos un reintento explícito.
          const im = data.implementacion;
          const inicio = Date.parse(im.gen_iniciado_en || "");
          const fresca = im.gen_estado === "generando" && Number.isFinite(inicio) && Date.now() - inicio < 300000;
          if (!fresca) { setGenDemorado(true); return; }
          data = await generarConReintento(false, false);
        } else {
          data = await generarConReintento();
        }
        if (cancelado || !data) return;
        setGenerando(false);
      }
      const im = data.implementacion;
      aplicarImpl(im);
      implCargada = true;
      try { const cl = await api.get("/clips", { params: { pantalla: "implementacion", forma: im.forma } }); setClips(cl.data.clips || {}); } catch {}
      // Si ya está andando, se muestra terminada (último paso, todos marcados). Nunca dispara el logro.
      // El paso inicial se calcula sobre el MISMO flujo estable (prep foto + checklist): si queda prep
      // pendiente, parás en el primer paso de prep sin hacer; si no, sumás forma_paso_actual (que es
      // relativo al checklist) al tamaño del flujo de preparación. Así el paso visible tras recargar es
      // el mismo que antes de recargar.
      const { prepFlow: pf0 } = computePrepFlow(im.preparacion || [], id, im.forma);
      const chk0 = im.checklist || [];
      const nPrepF = pf0.length;
      const primeraPend = pf0.findIndex((p) => !p.hecho);
      const flowLen = nPrepF + chk0.length;
      const pInicial = im.estado === "andando"
        ? (im.conector_ui ? nPrepF : Math.max(flowLen - 1, 0))
        : (primeraPend >= 0 ? primeraPend : nPrepF + Math.min(im.forma_paso_actual || 0, Math.max(chk0.length - 1, 0)));
      // Restauramos la posición EXACTA del usuario si la guardamos antes, para que recargar deje el
      // mismo paso visible (incluso si había retrocedido a un paso ya completado). El índice es
      // absoluto sobre el flow, que ahora es estable gracias a la foto de preparación.
      let pFinal = pInicial;
      if (im.estado !== "andando") {
        try {
          const saved = localStorage.getItem(`olimpo_paso_${id}${im.conector_ui ? "_conector_v1" : ""}`);
          if (saved != null) {
            const s = parseInt(saved, 10);
            if (Number.isFinite(s) && s >= 0 && s < flowLen) pFinal = s;
          }
        } catch { /* noop */ }
      }
      const conexionPendiente = pf0.findIndex((p) => p.prep_key === "chatgpt_conector" && !p.hecho);
      if (conexionPendiente >= 0) pFinal = primeraPend >= 0 ? primeraPend : conexionPendiente;
      setPasoActual(pFinal);
      setPasoTrabajo(Math.max(pFinal, primeraPend >= 0 ? primeraPend : 0));
      restauradoRef.current = true;
      if (im.ayuda_pedida && !im.ayuda_pedida.resuelta && typeof im.ayuda_pedida.paso_index === "number") {
        setNmsCount((c) => ({ ...c, [im.ayuda_pedida.paso_index]: 3 }));
      }
      const cb = await api.get("/cerebro");
      if (cancelado) return;
      setCerebro(cb.data);
      } catch (e) {
        // El error de carga solo aplica si todavía no cargó la implementación:
        // un fallo del dato de soporte (cerebro) no debe tapar una impl válida.
        if (!cancelado) {
          setGenerando(false);
          if (!implCargada) setCargaError(true);
        }
      }
    })();
    return () => { cancelado = true; if (genCancel.current) genCancel.current(); };
  }, [id, user]);

  // Guardamos la posición del usuario por proceso, para restaurarla al recargar/re-navegar.
  useEffect(() => {
    if (!id || !restauradoRef.current) return;
    try { localStorage.setItem(`olimpo_paso_${id}${impl?.conector_ui ? "_conector_v1" : ""}`, String(pasoActual)); } catch { /* noop */ }
  }, [pasoActual, id, impl?.conector_ui]);

  // La generación del pedido corre del lado del servidor (no depende de esta pantalla).
  // El error NO depende del reloj: mientras el servidor diga "generando" seguimos esperando.
  // La pantalla de fallo aparece solo si el servidor dice "fallo" (tras un reintento) o si el
  // servidor deja de responder varias veces seguidas. Tope duro de seguridad a los 5 minutos.
  const generarConReintento = (arrancar = true, reintentarAutomatico = true) => new Promise((resolve) => {
    let resuelto = false;
    let pollId = null;
    let reintentado = false;
    let fallosSeguidos = 0;
    const timers = [];
    const cleanup = () => { timers.forEach(clearTimeout); if (pollId) clearInterval(pollId); };
    const finish = (payload) => { if (!resuelto) { resuelto = true; cleanup(); resolve(payload); } };
    genCancel.current = () => finish(null);
    let consultando = false;
    const chequear = async () => {
      if (resuelto || consultando) return;
      consultando = true;
      try {
        const { data } = await api.get(`/implementacion/${id}`);
        if (resuelto) return;
        fallosSeguidos = 0;
        const im = data?.implementacion;
        // ORDEN IMPORTANTE: durante una regeneración el documento VIEJO sigue presente, así que
        // primero resolvemos los estados de recuperación (falta_marca / fallo) y recién después
        // tomamos el éxito por documento. Si no, el viejo se tomaría por éxito y ocultaría la pantalla
        // de recuperación.
        if (im?.gen_estado === "falta_marca") {
          setFaltaMarca(im.pregunta_marca || ""); setGenerando(false); finish(null); return;
        }
        if (im?.gen_estado === "fallo") {
          // El servidor avisó que falló: reintentamos UNA vez; si vuelve a fallar, recién ahí avisamos.
          if (!reintentado && reintentarAutomatico) { reintentado = true; api.post(`/implementacion/${id}/generar`).catch(() => {}); }
          else { setGenDemorado(true); finish(null); }
          return;
        }
        // Éxito: documento listo y NO en curso (el viejo durante regeneración queda con "generando").
        if (im?.documento && im?.gen_estado !== "generando") { finish({ implementacion: im }); return; }
        // gen_estado "generando" -> seguimos esperando, sin importar cuánto pasó (hasta el tope).
      } catch {
        // Sin respuesta del servidor: recién tras varios intentos seguidos lo tomamos como caído.
        fallosSeguidos += 1;
        if (!resuelto && fallosSeguidos >= 5) { setGenDemorado(true); finish(null); }
      } finally { consultando = false; }
    };
    const startPoll = () => { if (!pollId && !resuelto) { chequear(); pollId = setInterval(chequear, 4000); } };
    // A los 60s: línea de paciencia (NO es un error; la animación sigue corriendo).
    timers.push(setTimeout(() => { if (!resuelto) setPaciencia(true); }, 60000));
    // Tope duro a los 5 minutos: si sigue "generando", ahí sí es un problema real.
    timers.push(setTimeout(() => { if (!resuelto) { setGenDemorado(true); finish(null); } }, 300000));
    if (arrancar) {
      // Dispara la generación en el servidor (no bloqueante) y arranca a consultar el estado.
      api.post(`/implementacion/${id}/generar`)
        .then((r) => {
          if (resuelto) return;
          if (r.data?.gen_estado === "falta_marca") { setFaltaMarca(r.data.pregunta || ""); setGenerando(false); finish(null); }
          else if (r.data?.gen_estado === "listo" && r.data?.implementacion?.documento) finish({ implementacion: r.data.implementacion });
          else startPoll();
        })
        .catch(() => startPoll());
    } else {
      // No relanzamos: solo esperamos y sondeamos una generación que ya está en curso.
      startPoll();
    }
  });

  // El sello se muestra unos segundos y se colapsa solo.
  useEffect(() => {
    if (!impl) return;
    setSelloExpandido(true);
    const t = setTimeout(() => setSelloExpandido(false), 4500);
    return () => clearTimeout(t);
  }, [impl?.forma]);

  const flow = [...prepFlow, ...checklist];
  const nPrep = prepFlow.length;
  const total = flow.length;
  const current = flow[pasoActual];

  // Contexto del paso EN PANTALLA que viaja con cada mensaje del chat (para que el asistente sepa
  // dónde está parada la persona y no responda desde el paso 1).
  const pasoContexto = current ? {
    paso_num: pasoActual + 1,
    paso_total: total,
    paso_titulo: current.titulo || "",
    pasos_hechos: flow.slice(0, pasoActual).map((f) => f.titulo).filter(Boolean),
    nms_intentos: nmsCount[pasoActual] || 0,
  } : null;

  const persistir = async (nuevaChecklist, nuevoPaso) => {
    await api.patch(`/implementacion/${id}`, { checklist: nuevaChecklist, forma_paso_actual: nuevoPaso });
  };

  const avanzar = async (forzar = false) => {
    const cur = flow[pasoActual];
    if (!cur || avanzandoRef.current) return;
    avanzandoRef.current = true;
    setAvanzando(true);
    try {
    // Al pasar de paso, la pantalla arranca desde arriba del paso nuevo (no queda a mitad).
    const scrollArriba = () => { const el = document.querySelector('[data-testid="implementar-scroll"]'); if (el) el.scrollTo({ top: 0, behavior: "smooth" }); };
    // Paso de preparación: se marca hecho A NIVEL CUENTA (no se vuelve a pedir en el próximo proceso).
    if (cur.prep) {
      await api.post("/preparacion/hecho", { prep_key: cur.prep_key });
      track("preparacion_paso", { proceso_id: id, prep_key: cur.prep_key });
      setPrepFlow((pf) => pf.map((p) => (p.prep_key === cur.prep_key ? { ...p, hecho: true } : p)));
      setPrep((all) => all.map((p) => (p.prep_key === cur.prep_key ? { ...p, hecho: true } : p)));
      setDir(1);
      const otraPrep = prepFlow.findIndex((p, i) => i > pasoActual && !p.hecho);
      const retomarUso = nPrep + Math.min(impl.forma_paso_actual || 0, Math.max(checklist.length - 1, 0));
      const siguiente = cur.prep_key === "chatgpt_conector"
        ? Math.min(otraPrep >= 0 ? otraPrep : impl.estado === "andando" ? nPrep : retomarUso, total - 1)
        : Math.min(pasoActual + 1, total - 1);
      setPasoActual(siguiente);
      setPasoTrabajo((pt) => Math.max(pt, siguiente));
      setVerDoc(false); setAyuda(null);
      scrollArriba();
      return;
    }
    const ui = pasoActual - nPrep;  // índice dentro del checklist de uso
    // El paso de "pegámelo acá abajo" (criterio) es el momento más valioso: no se saltea sin pegar el resultado.
    if (cur?.criterio && !pegado[pasoActual]) {
      // Paso "Traeme lo que te respondió": avanza al pegar la respuesta. Si aprieta LISTO sin pegar,
      // le preguntamos UNA vez si prefiere saltearlo; recién ahí lo dejamos avanzar.
      if (cur?.traer_respuesta) {
        if (!forzar) {
          setPreguntarSaltear(true);
          return;
        }
        // forzar === true: eligió saltear igual -> seguimos al avance normal.
      } else {
        setChatAbierto(true);
        toast.error(`Pegá abajo lo que te devolvió ${iaNombre} para revisarlo. Este paso no se saltea.`);
        const el = document.querySelector('[data-testid="chat-input"]');
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
        return;
      }
    }
    if (cur?.conector) await api.post("/permisos/conector-conectado", { conector: cur.conector_nombre, proceso_id: id });
    if (cur?.computer_use) await api.post("/permisos/computer-use-activado", { proceso_id: id });
    const nueva = checklist.map((s, i) => (i === ui ? { ...s, listo: true } : s));
    const siguiente = Math.min(pasoActual + 1, total - 1);
    await persistir(nueva, Math.min(ui + 1, Math.max(checklist.length - 1, 0)));
    track("paso_listo", { proceso_id: id, paso: pasoActual + 1 });
    setDir(1);
    setChecklist(nueva);
    setPreguntarSaltear(false);
    setPasoActual(siguiente);
    setPasoTrabajo((pt) => Math.max(pt, siguiente));
    setVerDoc(false);
    setAyuda(null);
    scrollArriba();
    } catch (e) {
      toast.error("No se pudo guardar. Seguís en este paso; volvé a intentar.");
    } finally {
      avanzandoRef.current = false;
      setAvanzando(false);
    }
  };

  // "PEGAR LA RESPUESTA": mandamos lo pegado a la conversación para que el asistente lo comente
  // y con eso el paso queda dado (onEnviar marca pegado[pasoActual]).
  const pegarRespuesta = () => {
    const txt = (respuestaPegada || "").trim();
    if (!txt) {
      toast.error(`Pegá primero en la caja la respuesta que te dio ${iaNombre}.`);
      return;
    }
    setChatAbierto(true);
    setPreguntarSaltear(false);
    setInyectar({ texto: txt, nonce: Date.now() });
    setRespuestaPegada("");
  };

  const irAPaso = (i) => { if (avanzandoRef.current) return; setDir(i > pasoActual ? 1 : -1); setPasoActual(i); setVerDoc(false); setVerPasos(false); setAyuda(null); setPreguntarSaltear(false); setRespuestaPegada(""); const el = document.querySelector('[data-testid="implementar-scroll"]'); if (el) el.scrollTo({ top: 0, behavior: "smooth" }); };

  const noMeSale = (paso) => {
    const n = (nmsCount[pasoActual] || 0) + 1;
    track("no_me_sale", { proceso_id: id, paso: pasoActual + 1, intento: n });
    if (paso?.conector) api.post("/permisos/conector-no-me-sale", { conector: paso.conector_nombre, proceso_id: id }).catch(() => {});
    setNmsCount((c) => ({ ...c, [pasoActual]: n }));
    setChatAbierto(true);
    setNmsActivo(true);
    // PROMPT 7: desde el primer toque pedimos la captura de frente (con el botón visible abajo).
    const texto = `No me sale el paso "${paso.titulo}". Estoy trabado. Contame qué tendría que estar viendo y, si te sirve, pedime una captura de mi pantalla para que me digas exactamente dónde tocar.`;
    setInyectar({ texto, nonce: Date.now() });
  };

  const pedirAyuda = async (paso) => {
    try {
      const { data } = await api.post(`/implementacion/${id}/pedir-ayuda`, { paso_index: pasoActual, paso_titulo: paso.titulo });
      try { await navigator.clipboard.writeText(data.mensaje); } catch (e) { /* noop */ }
      setAyuda({ mensaje: data.mensaje, link: data.link_grupo });
      toast.success("Ya te lo copié. Pegalo en el grupo y mandalo.");
    } catch (e) {
      toast.error("No pude preparar el mensaje. Probá de nuevo.");
    }
  };

  const copiarChequeo = async (texto) => {
    await navigator.clipboard.writeText(texto);
    setChequeoCopiado(true);
    toast.success(`Copiado. Pegalo en ${iaNombre} para probar que quedó.`);
    setTimeout(() => setChequeoCopiado(false), 2200);
  };

  // Fuente de verdad de la implementación: relee del servidor y repinta (forma, checklist, probado,
  // estado). Al volver el foco NO tocamos el paso actual ni rearmamos el flujo: la persona sigue
  // exactamente en el paso donde estaba (ningún paso avanza solo). Solo con rebuild=true (cambio de
  // forma/camino) rearmamos los pasos y reposicionamos.
  const recargarImpl = useCallback(async (rebuild = false, avisar = false) => {
    try {
      const { data } = await api.get(`/implementacion/${id}`);
      const im = data.implementacion;
      if (!im) return;
      aplicarImpl(im, rebuild);
      if (rebuild) {
        const { prepFlow: pfR } = computePrepFlow(im.preparacion || [], id, im.forma);
        const flowLen = pfR.length + (im.checklist || []).length;
        const pReb = im.estado === "andando" ? Math.max(flowLen - 1, 0) : 0;
        setPasoActual(pReb);
        setPasoTrabajo(pReb);
      }
    } catch (e) { if (avisar) toast.error("No pude actualizar el resultado. Volvé a intentar."); }
  }, [id]);

  useRefetchOnFocus(recargarImpl, !!impl && !generando);

  const onAcciones = (data) => {
    if (data?.forma) {
      setImpl((im) => ({ ...im, conector_ui: undefined, requiere_control: undefined, aviso_cu: null, forma: data.forma.forma, forma_nombre: data.forma.forma_nombre, forma_motivo: data.forma.forma_motivo, checklist: data.forma.checklist }));
      setChecklist(data.forma.checklist || []);
      setPasoActual(0);
      setPasoTrabajo(0);
      toast(`Cambiamos a: ${data.forma.forma_nombre}`, { description: data.forma.forma_motivo });
      recargarImpl(true);  // la forma cambió: recargamos los pasos de preparación a medida
    }
    if (data?.probado === "verificado") {
      setImpl((im) => ({ ...im, probado: "verificado" }));
      if (impl?.estado !== "andando") toast.success("¡Salió! Ya podés ponerlo andando.");
    }
    // El asistente guardó algo en la implementación: releemos y la pantalla queda al día sola.
    if ((data?.actualizo || []).includes("implementacion")) recargarImpl();
  };

  const ponerAndando = async () => {
    try {
      const { data } = await api.post(`/implementacion/${id}/andando`);
      if (data?.mixto_siguiente) {
        toast.success("Primer pedido listo. Ahora vamos con el segundo.", {
          description: "Es otra herramienta, así que va en un pedido aparte.",
        });
        setDir(1);
        setPasoActual(0);
        setPasoTrabajo(0);
        setVerDoc(false);
        setVerPasos(false);
        setAyuda(null);
        await recargarImpl(true);
        return;
      }
      setLogro(data.logro || { nombre, horas_proceso: 0, horas_total: 0, andando_count: 1, total_focos: 1, es_primer: true, siguiente: null });
    } catch (e) {
      toast.error(errText(e) || "No pude ponerlo andando. Probá de nuevo en un ratito.");
    }
  };

  const registrarResultadoCliente = async () => {
    if (declarandoResultado) return;
    setDeclarandoResultado(true);
    try {
      const { data } = await api.post(`/implementacion/${id}/resultado-cliente`, { salio: true });
      setImpl((im) => ({ ...im, probado: "verificado", ultima_corrida: data.corrida || im.ultima_corrida }));
      toast.success("Listo. La prueba quedó confirmada.");
    } catch (e) {
      toast.error(errText(e) || "No pude guardar el resultado. Volvé a intentar.");
    } finally {
      setDeclarandoResultado(false);
    }
  };

  const seguirConSiguiente = async () => {
    try {
      const s = logro.siguiente;
      const { data } = await api.post(`/proceso`, { foco_id: s.foco_id });
      nav(`/proceso/${data.proceso.id}`);
    } catch (e) {
      toast.error("No pude abrir el próximo. Te llevo a tu mesa.");
      nav("/");
    }
  };

  // Al salir del logro: si se abrieron Las Puertas, esa pantalla manda; si no, el Momento.
  // La encuesta ya NO vive acá: aparece en la Mesa (después de IR A MI MESA), nunca encima del logro.
  const salirDeLogro = (accion) => {
    _continuarSalida(accion);
  };

  const _continuarSalida = (accion) => {
    if (logro?.puertas) { setPuertas(logro.puertas); setLogro(null); return; }
    const horasProc = Math.round(logro?.horas_proceso || 0);
    const horasTotal = Math.round(logro?.horas_total || 0);
    let mid = null, vars = {};
    if (logro?.es_primer) { mid = "m5"; vars = { horas: horasProc }; }
    else if ((logro?.andando_count || 0) >= 2) { mid = "m6"; vars = { horas: horasTotal, dias: Math.max(1, Math.round(horasTotal / 8)) }; }
    if (mid && pedir(mid, vars, accion)) { setLogro(null); return; }
    setLogro(null);
    accion();
  };

  const elegirOtraForma = async (letra) => {
    if (cambiandoForma) return;
    setCambiandoForma(true);
    try {
      const { data } = await api.post(`/implementacion/${id}/elegir-forma`, { forma: letra });
      setImpl({ ...data.implementacion, conector_ui: false, requiere_control: undefined, aviso_cu: null });
      setChecklist(data.implementacion.checklist || []);
      setPasoActual(0);
      setPasoTrabajo(0);
      setVerOpciones(false);
      toast.success("Listo, cambié el camino.");
      recargarImpl(true);  // recargamos los pasos de preparación de la nueva forma
    } catch (e) { toast.error("No pude cambiar el camino. Probá de nuevo."); }
    finally { setCambiandoForma(false); }
  };

  // Reintento REAL: consulta el estado ANTES de mandar POST /generar. Si ya hay documento, lo muestra;
  // si sigue generando, NO relanza (solo vuelve a esperar y sondear); solo genera de nuevo si falló.
  const reintentarGeneracion = async () => {
    if (genCancel.current) genCancel.current();
    setGenDemorado(false);
    setPaciencia(false);
    setGenerando(true);
    // El POST normal reusa sólo un pedido listo. El servidor conserva la generación fresca
    // en curso y recupera una fallida o vencida; nunca decide por la presencia del documento viejo.
    const data = await generarConReintento();
    if (data?.implementacion?.documento) {
      window.location.replace(window.location.pathname); // no repetir regenerar=1 al recargar
    }
  };

  if (faltaMarca !== null) {
    const enviarMarca = async () => {
      const marca = marcaTexto.trim();
      if (!marca || enviandoMarca) return;
      setEnviandoMarca(true);
      try {
        await api.post(`/implementacion/${id}/marca-ventana`, { marca });
        window.location.reload();
      } catch (e) {
        setEnviandoMarca(false);
        toast.error(errText(e) || "No pude guardarlo. Probá de nuevo.");
      }
    };
    return (
      <div className="min-h-screen bg-negro flex items-center justify-center px-6" data-testid="falta-marca">
        <div className="max-w-lg w-full space-y-5">
          <p className="text-crema text-base leading-relaxed whitespace-pre-line" data-testid="falta-marca-pregunta">
            {faltaMarca || "Antes de armarte el pedido necesito saber por qué marca concreta se distingue una ventana de la otra: el nombre del perfil arriba a la derecha, el título de la pestaña, o el nombre del proyecto en la pantalla."}
          </p>
          <textarea
            value={marcaTexto}
            onChange={(e) => setMarcaTexto(e.target.value)}
            placeholder="Ej: el nombre del proyecto arriba a la derecha, en el perfil de Chrome."
            className="w-full rounded-xl bg-transparent text-crema p-3 text-sm leading-relaxed outline-none"
            style={{ border: "1px solid #2A2A2A", minHeight: 96, resize: "vertical" }}
            data-testid="falta-marca-input"
          />
          <button className="btn-dorado" onClick={enviarMarca} disabled={enviandoMarca || !marcaTexto.trim()} data-testid="falta-marca-enviar">
            {enviandoMarca ? "ARMANDO…" : "CON ESTO, ARMALO"}
          </button>
          <button className="mono-label text-gris underline" onClick={() => nav(`/proceso/${id}`)} data-testid="marca-volver-proceso">VOLVER AL PROCESO</button>
          <PedidoAnterior texto={pedidoAnterior} />
        </div>
      </div>
    );
  }
  if (!impl && generando) {
    if (genDemorado) {
      return (
        <div className="min-h-screen bg-negro flex items-center justify-center px-6" data-testid="gen-demorado">
          <div className="max-w-md text-center space-y-6">
            <p className="text-crema text-xl leading-relaxed">
              {pedidoAnterior ? "La actualización del pedido no terminó. Podés reintentar o revisar la versión anterior." : "Se nos trabó al armar tu pedido. Dale de nuevo y lo resolvemos."}
            </p>
            <button className="btn-dorado" onClick={reintentarGeneracion} data-testid="gen-recargar">
              VOLVER A INTENTAR
            </button>
            <button className="mono-label text-gris underline" onClick={() => nav(`/proceso/${id}`)} data-testid="gen-volver-proceso">VOLVER AL PROCESO</button>
            <PedidoAnterior texto={pedidoAnterior} />
          </div>
        </div>
      );
    }
    return <GenerandoImplementacion paciencia={paciencia} />;
  }
  if (cargaError) {
    return (
      <div className="min-h-screen bg-negro flex items-center justify-center px-6" data-testid="implementar-error">
        <div className="max-w-md text-center space-y-6">
          <p className="text-crema text-xl leading-relaxed">No pudimos abrir esta implementación. Puede que ya no exista o que haya un problema de conexión.</p>
          <button className="btn-dorado" onClick={() => nav("/")} data-testid="implementar-error-volver">IR A MI MESA</button>
        </div>
      </div>
    );
  }
  if (!impl) return <LoaderOlimpo fullscreen />;

  const verificado = impl.probado === "verificado";
  const esSetter = !!impl.setter;
  const andando = impl.estado === "andando";
  const splitStep = current && current.doc;

  const slide = {
    initial: { opacity: 0, x: dir * 40 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: dir * -40 },
    transition: { duration: 0.32 },
  };

  return (
    <div className="h-screen overflow-hidden bg-negro flex flex-col" data-testid="implementar-root"
      data-paso-actual={pasoActual} data-flow-total={total} data-current-titulo={current?.titulo || ""}
      data-current-tipo={current?.prep ? "prep" : current?.doc ? "doc" : current?.criterio ? "criterio" : current?.prueba ? "prueba" : "uso"}>
      <header className="h-14 border-b border-borde flex items-center justify-between px-6 shrink-0">
        <Logo variant="mark" height={24} />
        <button onClick={() => nav("/")} className="mono-label text-gris hover:text-crema flex items-center gap-2 transition-colors" data-testid="volver-mesa">
          <ArrowLeft size={14} /> MI MESA
        </button>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto w-full px-6 py-8 flex flex-col" data-testid="implementar-scroll">
        {andando && (
          <div className="max-w-[1080px] mx-auto w-full mb-6 rounded-xl p-4 flex items-center gap-3" style={{ border: "1px solid rgba(111,191,169,0.5)", background: "rgba(111,191,169,0.08)" }} data-testid="implementar-andando-banner">
            <Check size={18} className="text-verde shrink-0" />
            <div>
              <div className="mono-label text-verde">PROCESO ANDANDO</div>
              <p className="text-crema text-sm">Este proceso ya está funcionando. Podés usarlo con datos nuevos desde el primer paso o repasar las indicaciones en <b>ver todos los pasos</b>.</p>
            </div>
          </div>
        )}
        {impl.plan_estado?.sin_pago && (
          <div className="max-w-[1080px] mx-auto w-full mb-6 rounded-xl p-4" style={{ border: "1px solid rgba(236,131,90,0.45)", background: "rgba(236,131,90,0.08)" }} data-testid="implementar-barra-plan">
            <p className="text-crema text-sm mb-3">
              {impl.forma === "F"
                ? (impl.conector_ui && !impl.requiere_control ? "Tu cuenta de ChatGPT necesita tener disponibles Work y la conexión de Olimpo. Si no aparecen, pedí ayuda para revisar el acceso de tu cuenta antes de elegir otro plan." : "Para poner esto a andar te falta un plan de ChatGPT con el control de la computadora disponible. Todo lo demás lo podés seguir haciendo mientras tanto.")
                : "Para poner esto a andar te falta el plan pago de Claude. Todo lo demás lo podés seguir haciendo mientras tanto."}
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <a href={impl.forma === "F" ? "https://openai.com/chatgpt/pricing" : "https://claude.com/pricing"} target="_blank" rel="noreferrer" className="btn-dorado inline-flex items-center gap-2 text-xs" data-testid="barra-plan-ver">VER LOS PLANES</a>
            </div>
            <div className="mt-3 pt-3 border-t border-borde/50">
              <p className="text-gris text-xs mb-2">Mientras tanto podés seguir definiendo procesos. Cuanto más tengas escrito, más rápido va a ser cuando arranques.</p>
              <button onClick={() => nav("/diagnostico?agregar=1")} className="mono-label text-dorado hover:underline" style={{ fontSize: "10px" }} data-testid="barra-plan-definir">DEFINIR OTRO PROCESO</button>
            </div>
          </div>
        )}
        {/* PEDIDOS DOBLES · aviso de proceso mixto */}
        {impl.mixto && Array.isArray(impl.pedidos) && impl.pedidos.length > 1 && (
          <div className="max-w-[1080px] mx-auto w-full mb-6 rounded-xl p-4" style={{ border: "1px solid rgba(201,168,76,0.45)", background: "rgba(201,168,76,0.07)" }} data-testid="mixto-aviso">
            <div className="mono-label text-dorado mb-1" data-testid="mixto-progreso">
              PEDIDO {(impl.pedido_actual || 0) + 1} DE {impl.pedidos.length} · {impl.pedidos[impl.pedido_actual || 0]?.ia || (impl.forma === "F" ? "ChatGPT" : "Claude")}
            </div>
            <p className="text-crema text-sm">
              {(impl.pedido_actual || 0) === 0
                ? "Tu proceso usa dos herramientas distintas y cada una se resuelve de una manera. Son dos pedidos y van por separado. Empezá por el primero."
                : "Terminaste el primer pedido. Este es el segundo y último: es otra herramienta, así que va por separado."}
            </p>
          </div>
        )}
        {/* Sello + barra de segmentos */}
        <div className="max-w-[1080px] mx-auto w-full mb-10">
          {impl.ordinal >= 2 && (
            <p className="text-dorado text-sm mb-3" data-testid="ordinal-reconocimiento">
              {impl.ordinal === 2
                ? "Segundo proceso. Este te va a salir más rápido."
                : impl.ordinal === 3 && impl.conector_ui
                  ? "Este es tu tercer proceso. Ahora conectamos Olimpo con ChatGPT una sola vez."
                  : "Ya venís con la mano. Este es rápido."}
            </p>
          )}
          <SelloForma tag={impl.conector_ui && !impl.requiere_control ? "CHATGPT · OLIMPO" : FORMA_TAG[impl.forma] || "IMPLEMENTAR"} motivo={impl.forma_motivo} expandido={selloExpandido} setExpandido={setSelloExpandido} />

          {impl.oportunidad?.problema && (
            <div className="mt-4 rounded-xl p-4" style={{ border: "1px solid rgba(236,131,90,0.45)", background: "rgba(236,131,90,0.08)" }} data-testid="implementar-honesto">
              <p className="text-crema text-sm mb-2">
                Te soy honesto: esto, tal como lo hacés hoy, no lo resolvemos del todo con las herramientas que tenés. El problema puntual es: {impl.oportunidad.problema}.
              </p>
              {impl.oportunidad.que_haria_falta && (
                <p className="text-gris text-xs">Lo dejamos anotado como algo a resolver a medida: {impl.oportunidad.que_haria_falta}. Igual, abajo te dejo lo que SÍ podemos sacarte de encima ahora.</p>
              )}
            </div>
          )}

          {Array.isArray(impl.opciones_forma) && impl.opciones_forma.length > 1 && (
            <div className="mt-4" data-testid="implementar-opciones">
              <button onClick={() => setVerOpciones((v) => !v)} className="mono-label text-dorado hover:underline" style={{ fontSize: "10px" }} data-testid="ver-opciones">
                {verOpciones ? "ocultar los caminos" : `hay ${impl.opciones_forma.length} caminos · ver opciones`}
              </button>
              {verOpciones && (
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  {impl.opciones_forma.map((o) => {
                    const actual = (o.forma || "").toUpperCase() === (impl.forma || "").toUpperCase();
                    return (
                      <div key={o.forma} className="card-dark p-4 flex flex-col" style={{ borderColor: o.recomendada ? "rgba(201,168,76,0.5)" : undefined }} data-testid={`opcion-${o.forma}`}>
                        {o.recomendada && <span className="mono-label text-dorado" style={{ fontSize: "9px" }}>RECOMENDADA</span>}
                        <p className="text-crema text-sm font-medium mt-1">{o.que_resuelve}</p>
                        <p className="text-gris text-xs mt-2"><b>Trabajo:</b> {o.trabajo}</p>
                        <p className="text-gris text-xs mt-1"><b>Queda a mano:</b> {o.queda_a_mano}</p>
                        <p className="text-gris text-xs mt-1"><b>Conviene si:</b> {o.cuando_conviene}</p>
                        <div className="mt-3">
                          {actual ? (
                            <span className="mono-label text-dorado" style={{ fontSize: "9px" }} data-testid={`opcion-${o.forma}-actual`}>ESTE ESTÁS USANDO</span>
                          ) : (
                            <button onClick={() => elegirOtraForma(o.forma)} disabled={cambiandoForma} className="btn-fantasma text-xs w-full" data-testid={`opcion-${o.forma}-elegir`}>
                              {cambiandoForma ? "Cambiando…" : "Elegir este"}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-4 mt-5">
            <div className="flex-1 flex gap-1.5" data-testid="segmentos">
              {flow.map((step, i) => {
                const stepDone = andando || i < pasoActual || (step.prep ? step.hecho : step.listo);
                const activo = i === pasoActual;
                return (
                  <div key={i} data-testid={`segmento-${i}`} className="h-1.5 flex-1 rounded-full transition-all duration-300"
                    style={{
                      background: stepDone || activo ? "#C9A84C" : "#2A2A2A",
                      opacity: stepDone ? 1 : activo ? 1 : 0.45,
                      boxShadow: activo ? "0 0 8px rgba(201,168,76,0.6)" : "none",
                    }} />
                );
              })}
            </div>
            <span className="mono-label text-dorado shrink-0" data-testid="paso-progreso">PASO {pasoActual + 1} DE {total}</span>
          </div>
          <div className="flex items-center gap-4 mt-3">
            {!andando && pasoActual > 0 && (
              <button onClick={() => irAPaso(pasoActual - 1)} className="mono-label text-gris hover:text-crema underline flex items-center gap-1 transition-colors" data-testid="volver-paso-anterior" style={{ fontSize: "10px" }}>
                <ArrowLeft size={12} /> PASO ANTERIOR
              </button>
            )}
            <button onClick={() => setVerPasos(true)} className="mono-label text-gris hover:text-crema underline transition-colors" data-testid="ver-todos-pasos" style={{ fontSize: "10px" }}>
              ver todos los pasos
            </button>
          </div>

          {/* Preparación ya hecha en procesos anteriores: colapsada en una línea, desplegable. */}
          {prepColapsados.length > 0 && (
            <div className="mt-5 rounded-xl overflow-hidden" style={{ border: "1px solid rgba(111,191,169,0.35)", background: "rgba(111,191,169,0.05)" }} data-testid="prep-colapsada">
              <button onClick={() => setPrepBannerAbierto((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="prep-colapsada-toggle">
                <Check size={16} className="text-verde shrink-0" />
                <span className="text-crema text-sm flex-1">Repasar la preparación</span>
                <ChevronDown size={16} className={`text-gris transition-transform ${prepBannerAbierto ? "rotate-180" : ""}`} />
              </button>
              {prepBannerAbierto && (
                <div className="px-4 pb-3 space-y-1.5" data-testid="prep-colapsada-lista">
                  {prepColapsados.map((p) => (
                    <button key={p.prep_key} onClick={() => setPeekPrep(p)} className="flex items-center gap-2 text-crema text-base underline min-h-[44px] text-left" data-testid={`repasar-prep-${p.prep_key}`}>
                      <Check size={14} className="text-verde shrink-0" /> {p.titulo}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cuando la persona está mirando un paso que ya hizo: franja para volver a donde estaba. */}
        {!andando && pasoActual !== pasoTrabajo && (
          <div className="max-w-[620px] mx-auto w-full px-6 mb-4">
            <div className="flex items-center justify-between gap-3 rounded-xl px-4 py-3"
              style={{ border: "1px solid rgba(201,168,76,0.4)", background: "rgba(201,168,76,0.06)" }}
              data-testid="mirando-paso-hecho">
              <span className="text-crema text-sm">Estás mirando un paso que ya hiciste.</span>
              <button onClick={() => irAPaso(pasoTrabajo)} className="btn-dorado !py-1.5 !px-3 text-xs shrink-0" data-testid="volver-a-donde-estaba">
                VOLVER A DONDE ESTABA
              </button>
            </div>
          </div>
        )}

        {/* La carta del paso actual, sola y centrada */}
        <div className="flex-1 flex items-start justify-center">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div key={pasoActual} {...slide} className="w-full flex justify-center">
              {splitStep ? (
                <div className="w-full max-w-[880px] mx-auto" data-testid="doc-layout">
                  <DocumentoPanel impl={impl} setImpl={setImpl} id={id} nombre={nombre} />
                  <div className="mt-10 pt-8 border-t border-borde/40" data-testid="doc-controles">
                    {renderPasoControles(current)}
                  </div>
                </div>
              ) : (
                <div className="w-full max-w-[620px]">{renderCard()}</div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Chat: franja fina abajo, cerrada por defecto. Vive en el flujo de la columna (shrink-0),
          nunca flota por encima del contenido: el área de arriba scrollea sola. */}
      <div className="shrink-0 max-w-[620px] mx-auto w-full px-6 pt-3 pb-6 border-t border-borde/40" data-testid="chat-strip">
        {!chatAbierto ? (
          <button onClick={() => setChatAbierto(true)} className="w-full card-dark px-5 py-3 flex items-center gap-3 text-gris hover:text-crema hover:border-dorado/40 transition-colors" data-testid="chat-abrir">
            <MessageCircle size={16} className="text-dorado" /> ¿Dudas? Escribime — también podés mandarme una captura
          </button>
        ) : (
          <div className="card-dark p-4" data-testid="chat-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="mono-label text-gris">ASISTENTE DE IMPLEMENTACIÓN</div>
              <button onClick={() => setChatAbierto(false)} className="text-gris hover:text-crema transition-colors" data-testid="chat-cerrar"><ChevronDown size={18} /></button>
            </div>
            <div className="h-[380px]">
              <Chat contexto="implementacion" procesoId={id} placeholder="Contame cómo salió o mandame una captura…" permitirAdjuntar capturaVisible={nmsActivo} onAcciones={onAcciones} onAvanzar={(tipo) => { if (tipo === "poner_andando") ponerAndando(); }} onEnviar={() => setPegado((p) => ({ ...p, [pasoActual]: true }))} inyectar={inyectar} pasoContexto={pasoContexto} />
            </div>
          </div>
        )}
      </div>

      {/* Documento a pantalla completa (mobile o botón) */}
      <AnimatePresence>
        {verDoc && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-negro/90 flex justify-end" onClick={() => setVerDoc(false)} data-testid="ver-documento-overlay">
            <motion.div initial={{ x: 60 }} animate={{ x: 0 }} exit={{ x: 60 }} className="w-full max-w-xl h-full bg-negro border-l border-borde p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-end mb-4">
                <button onClick={() => setVerDoc(false)} className="text-gris hover:text-crema transition-colors" data-testid="ver-documento-cerrar"><X size={20} /></button>
              </div>
              <DocumentoPanel impl={impl} setImpl={setImpl} id={id} nombre={nombre} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Todos los pasos (capa encima, cerrada por defecto) */}
      <AnimatePresence>
        {verClips && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-negro/95 flex items-start justify-center p-6 overflow-y-auto" onClick={() => setVerClips(false)} data-testid="ver-clips-overlay">
            <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <div className="mono-label text-dorado">TODOS LOS CLIPS, SEGUIDOS</div>
                <button onClick={() => setVerClips(false)} className="text-gris hover:text-crema transition-colors" data-testid="ver-clips-cerrar"><X size={18} /></button>
              </div>
              <div className="space-y-6">
                {Object.keys(clips).map(Number).filter((n) => !isNaN(n)).sort((a, b) => a - b).map((n) => (
                  <div key={n}>
                    <div className="mono-label text-gris mb-1" style={{ fontSize: "10px" }}>PASO {n + 1}{checklist[n]?.titulo ? ` · ${checklist[n].titulo}` : ""}</div>
                    <ClipVideo clipId={clips[String(n)]} />
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {verPasos && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-negro/90 flex items-center justify-center p-6" onClick={() => setVerPasos(false)} data-testid="pasos-overlay">
            <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-md bg-negro2 border border-borde rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <div className="mono-label text-dorado">TODOS LOS PASOS</div>
                <button onClick={() => setVerPasos(false)} className="text-gris hover:text-crema transition-colors" data-testid="pasos-cerrar"><X size={18} /></button>
              </div>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                {/* Preparación ya hecha en procesos anteriores: grupo con subpasos clickeables para repasarlos. */}
                {prepColapsados.length > 0 && (
                  <div className="rounded-lg overflow-hidden mb-1" style={{ border: "1px solid rgba(111,191,169,0.35)", background: "rgba(111,191,169,0.05)" }} data-testid="pasos-grupo-prep">
                    <div className="flex items-center gap-2 px-4 py-2.5">
                      <Check size={14} className="text-verde shrink-0" />
                      <span className="text-crema text-sm">Repasar la preparación</span>
                    </div>
                    <div className="px-2 pb-2 space-y-1">
                      {prepColapsados.map((p) => (
                        <button key={p.prep_key} onClick={() => { setPeekPrep(p); setVerPasos(false); }}
                          className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-verde/5 transition-colors"
                          data-testid={`paso-lista-prep-${p.prep_key}`}>
                          <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(111,191,169,0.18)", border: "1px solid rgba(111,191,169,0.6)" }}>
                            <Check size={11} className="text-verde" />
                          </span>
                          <span className="text-gris text-sm flex-1">{p.titulo}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {flow.map((s, i) => {
                  const done = andando || i < pasoActual || (s.prep ? s.hecho : s.listo);
                  const activo = i === pasoActual;
                  const puedeIr = done || activo;
                  return (
                    <button key={i} onClick={() => puedeIr ? irAPaso(i) : null} disabled={!puedeIr}
                      className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activo ? "bg-dorado/10 border border-dorado/50" : done ? "hover:bg-dorado/5" : "opacity-40 cursor-default"}`}
                      data-testid={`paso-lista-${i}`}>
                      <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={done ? { background: "rgba(201,168,76,0.18)", border: "1px solid #C9A84C" } : { border: "1px solid #3C3C3C" }}>
                        {done ? <Check size={13} className="text-dorado" /> : <span className="mono-label text-gris" style={{ fontSize: "10px" }}>{i + 1}</span>}
                      </span>
                      <span className={`flex-1 ${done || activo ? "text-crema text-sm" : "text-gris text-sm"}`}>{s.titulo}</span>
                      {activo && <span className="mono-label text-dorado shrink-0" style={{ fontSize: "9px", letterSpacing: "0.12em" }} data-testid={`paso-lista-actual-${i}`}>ACÁ ESTÁS</span>}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Repasar un paso de preparación ya hecho (no está en el flujo del proceso actual). */}
      <AnimatePresence>
        {peekPrep && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-negro/95 overflow-y-auto" data-testid="peek-prep-overlay">
            <div className="max-w-[620px] mx-auto w-full px-6 py-8">
              <div className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 mb-6" style={{ border: "1px solid rgba(201,168,76,0.4)", background: "rgba(201,168,76,0.06)" }} data-testid="peek-prep-banner">
                <span className="text-crema text-sm">Estás mirando un paso que ya hiciste.</span>
                <button onClick={() => setPeekPrep(null)} className="btn-dorado !py-1.5 !px-3 text-xs shrink-0" data-testid="peek-volver-a-donde-estaba">
                  VOLVER A DONDE ESTABA
                </button>
              </div>
              <PrepVideo video={peekPrep.video} />
              <h2 className="titulo mb-4" style={{ fontSize: "34px", lineHeight: 1.1 }}>{peekPrep.titulo}</h2>
              {peekPrep.prep_key === "chatgpt_conector" ? <GuiaConexionOlimpo /> : peekPrep.detalle && (
                <p className="text-crema leading-relaxed mb-8 text-[15px]" style={{ whiteSpace: "pre-line" }} data-testid="peek-prep-detalle">{peekPrep.detalle}</p>
              )}
              {peekPrep.prep_key !== "chatgpt_conector" && peekPrep.link && (
                <a href={peekPrep.link} target="_blank" rel="noreferrer" className="btn-fantasma inline-flex items-center gap-2 text-xs mb-7" data-testid="peek-prep-link">
                  {peekPrep.link_texto || "ABRIR"}
                </a>
              )}
              {peekPrep.prep_key !== "chatgpt_conector" && peekPrep.anticipacion && (
                <div className="text-gris text-sm mb-7 border-l-2 border-dorado/40 pl-3">
                  <span className="text-dorado">Lo que vas a ver: </span>{peekPrep.anticipacion}
                </div>
              )}
              {peekPrep.prep_key !== "chatgpt_conector" && peekPrep.por_que && (
                <div className="text-gris text-sm mb-7"><span className="text-crema">Por qué importa: </span>{peekPrep.por_que}</div>
              )}
              {peekPrep.prep_key !== "chatgpt_conector" && peekPrep.nota && (
                <div className="rounded-xl p-4 mb-7 text-gris text-sm" style={{ border: "1px solid #3C3C3C", background: "rgba(255,255,255,0.03)" }}>{peekPrep.nota}</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pantalla de logro */}
      <AnimatePresence>
        {logro && <LogroScreen key="logro" logro={logro} onSeguir={() => salirDeLogro(seguirConSiguiente)} onMañana={() => salirDeLogro(() => nav("/"))} />}
        {puertas && <PuertasScreen key="puertas" data={puertas} onSeguir={async () => { setPuertas(null); try { await refreshMe(); } catch {} nav("/operacion"); }} />}
      </AnimatePresence>
    </div>
  );

  // --- render de la carta del paso (usa el closure del componente) ---
  function renderAyudaEquipo(s) {
    return <>
            {(nmsCount[pasoActual] || 0) >= (s.prep ? 1 : 3) && (
              <button onClick={() => pedirAyuda(s)} className="btn-fantasma flex items-center gap-2 !border-verde/60 !text-verde mt-4" data-testid="pedir-ayuda-equipo">
                <MessageCircle size={15} /> PEDIR AYUDA AL EQUIPO
              </button>
            )}
            {ayuda && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 rounded-xl p-4" style={{ border: "1px solid rgba(111,191,169,0.5)", background: "rgba(111,191,169,0.06)" }} data-testid="ayuda-box">
                <p className="text-crema text-sm mb-3">
                  {ayuda.link ? "Ya te lo copié. Tocá el botón, pegalo en el grupo y mandalo." : "Copiá esto y mandalo al grupo de WhatsApp que armamos con vos."}
                </p>
                <div className="card-dark p-3 mb-3 text-crema text-sm" style={{ whiteSpace: "pre-wrap" }} data-testid="ayuda-mensaje">{ayuda.mensaje}</div>
                <div className="flex gap-3 flex-wrap">
                  <button onClick={() => copiarChequeo(ayuda.mensaje)} className="btn-fantasma flex items-center gap-2" data-testid="ayuda-copiar">
                    <Copy size={14} /> COPIAR DE NUEVO
                  </button>
                  {ayuda.link && (
                    <a href={ayuda.link} target="_blank" rel="noreferrer" className="btn-dorado flex items-center gap-2" data-testid="ayuda-abrir-grupo">
                      <MessageCircle size={14} /> ABRIR MI GRUPO DE WHATSAPP
                    </a>
                  )}
                </div>
              </motion.div>
            )}
    </>;
  }

  function renderResultadoConector() {
    return <>
            {impl.conector_ui && (
              <div className="rounded-xl border border-dorado/30 p-4 space-y-3" data-testid="resultado-conector">
                {impl.ultima_corrida ? <>
                  <p className="text-crema">{impl.ultima_corrida.salio ? "Prueba registrada: salió bien" : "Prueba registrada: necesita corrección"}</p>
                  <p className="text-gris text-sm whitespace-pre-wrap">{impl.ultima_corrida.detalle}</p>
                  <p className="text-gris text-xs">{new Date(impl.ultima_corrida.creado_en).toLocaleString()}</p>
                </> : <p className="text-gris text-sm">Todavía no hay una prueba registrada. Cuando revises el resultado, pedile a ChatGPT que lo registre en Olimpo.</p>}
                <button className="btn-fantasma text-xs" onClick={() => recargarImpl(false, true)} data-testid="actualizar-resultado">ACTUALIZAR RESULTADO</button>
              </div>
            )}
    </>;
  }

  function renderPasoControles(s) {
    return (
      <>
        {andando ? (
          <div className="space-y-4" data-testid="paso-andando">
            <p className="text-crema text-base">{impl.conector_ui ? "El proceso sigue andando. Hacé cada nuevo caso desde ChatGPT y revisá su resultado. Acá podés consultar lo último que quedó registrado." : "Ya está andando. Esto es solo para repasar cómo quedó."}</p>
            {renderResultadoConector()}
            <button onClick={() => nav("/")} className="btn-dorado flex items-center gap-2" data-testid="andando-ir-mesa">
              <ArrowLeft size={15} /> IR A MI MESA
            </button>
          </div>
        ) : s.prueba ? (
          <div className="space-y-4" data-testid="paso-prueba">
            {renderResultadoConector()}
            {!verificado ? (
              impl.forma === "F" && !impl.conector_ui ? (
              <div className="flex flex-wrap items-center gap-4">
                <button onClick={registrarResultadoCliente} disabled={declarandoResultado} className="btn-dorado flex items-center gap-2" data-testid="prueba-salio-bien">
                  <Check size={15} /> {declarandoResultado ? "GUARDANDO…" : impl.setter ? "SÍ, LOS 3 SALIERON BIEN" : "SÍ, SALIÓ BIEN"}
                </button>
                <button onClick={() => setChatAbierto(true)} className="btn-fantasma flex items-center gap-2" data-testid="prueba-necesito-ayuda">
                  <HelpCircle size={15} /> NECESITO AYUDA
                </button>
              </div>
              ) : (<>
                <button onClick={() => setChatAbierto(true)} className="btn-dorado flex items-center gap-2" data-testid="prueba-contar-btn">
                  <MessageCircle size={15} /> CONTARME CÓMO SALIÓ
                </button>
              </>)
            ) : (
              <button onClick={ponerAndando} className="btn-dorado flex items-center gap-2" data-testid="poner-andando">
                <Check size={15} /> YA FUNCIONA, PONELO ANDANDO
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-4">
              <button onClick={() => avanzar()} disabled={avanzando} className={`flex items-center gap-2 ${current?.criterio && !pegado[pasoActual] ? "btn-fantasma" : "btn-dorado"}`} data-testid="paso-listo">
                <Check size={15} /> {avanzando ? "GUARDANDO…" : s.prep_key === "chatgpt_conector" ? "USAR MI PROCESO" : impl.conector_ui && s.key === "f_consultar" ? "YA VEO MI PROCESO, SEGUIR" : impl.conector_ui && s.key === "f_probar" ? "YA TENGO EL RESULTADO, SEGUIR" : impl.conector_ui && s.key === "corregir_probar" ? "YA LO REVISÉ, SEGUIR" : pasoActual >= total - 1 ? "LISTO" : "LISTO, SIGUIENTE"}
              </button>
              <button onClick={() => noMeSale(s)} title="¿Trabado? Contame o mandame una captura de tu pantalla." className="mono-label text-gris hover:text-crema flex items-center gap-1.5 transition-colors" data-testid="paso-no-me-sale">
                <HelpCircle size={14} /> NO ME SALE
              </button>
            </div>
            {s.traer_respuesta && preguntarSaltear && !pegado[pasoActual] && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl p-4 mt-4" style={{ border: "1px solid rgba(201,168,76,0.5)", background: "rgba(201,168,76,0.06)" }} data-testid="saltear-pregunta">
                <p className="text-crema text-sm mb-3">Todavía no me pegaste la respuesta. Este paso avanza cuando me la pegás acá arriba, así te confirmo si quedó bien. ¿Preferís saltearlo igual?</p>
                <div className="flex gap-4 flex-wrap items-center">
                  <button onClick={() => avanzar(true)} className="btn-fantasma flex items-center gap-2 text-xs" data-testid="saltear-confirmar">
                    SALTEAR IGUAL
                  </button>
                  <button onClick={() => setPreguntarSaltear(false)} className="mono-label text-gris hover:text-crema text-xs transition-colors" data-testid="saltear-cancelar">
                    no, lo pego
                  </button>
                </div>
              </motion.div>
            )}
            {s.prep_key !== "chatgpt_conector" && renderAyudaEquipo(s)}
          </>
        )}
      </>
    );
  }

  function renderCard() {
    const s = current;
    if (!s) return null;
    const usageIdx = pasoActual - nPrep;
    const clipsOrden = Object.keys(clips).map(Number).filter((n) => !isNaN(n)).sort((a, b) => a - b);
    if (s.prep_key === "chatgpt_conector") return <div>
      <h2 className="titulo mb-4" style={{ fontSize: "34px", lineHeight: 1.1 }} data-testid="paso-titulo">{s.titulo}</h2>
      <PrepVideo video={s.video} />
      <GuiaConexionOlimpo accionesFinales={renderPasoControles(s)} onComprobado={s.hecho ? undefined : () => avanzar()} onAyuda={() => noMeSale(s)} />
      {renderAyudaEquipo(s)}
    </div>;
    return (
      <div>
        {s.prep ? (
          <PrepVideo video={s.video} />
        ) : (
          <>
            {clipsOrden.length >= 2 && (
              <button onClick={() => { setVerClips(true); track("ver_todo_seguido", { pantalla: "implementacion", forma: impl?.forma || null }); }} className="mono-label text-dorado hover:underline mb-3" style={{ fontSize: "10px" }} data-testid="ver-todo-seguido">
                ver todo seguido
              </button>
            )}
            <ClipVideo clipId={clips[String(usageIdx)]} pantalla="implementacion" forma={impl?.forma || null} paso_key={String(usageIdx)} />
          </>
        )}
        <h2 className="titulo mb-4" style={{ fontSize: "34px", lineHeight: 1.1 }} data-testid="paso-titulo">{s.titulo}</h2>
        <p className="text-crema leading-relaxed mb-8 text-[15px]" style={{ whiteSpace: "pre-line" }} data-testid="paso-detalle">{s.detalle}</p>

        {s.prep && s.link && (
          <a href={s.link} target="_blank" rel="noreferrer" className="btn-fantasma inline-flex items-center gap-2 text-xs mb-7" data-testid="paso-link">
            {s.link_texto || "ABRIR"}
          </a>
        )}

        {s.anticipacion && (
          <div className="text-gris text-sm mb-7 border-l-2 border-dorado/40 pl-3" data-testid="paso-anticipacion">
            <span className="text-dorado">Lo que vas a ver: </span>{s.anticipacion}
          </div>
        )}

        {s.por_que && (
          <div className="text-gris text-sm mb-7" data-testid="paso-porque">
            <span className="text-crema">{s.conector ? "Por qué te lo pide: " : "Por qué importa: "}</span>{s.por_que}
          </div>
        )}

        {s.empresa_admin && s.mensaje_admin && (
          <div className="rounded-xl p-4 mb-7" style={{ border: "1px solid rgba(236,131,90,0.45)", background: "rgba(236,131,90,0.07)" }} data-testid="paso-empresa-admin">
            {s.empresa_nota && <p className="text-crema text-sm mb-3">{s.empresa_nota}</p>}
            <div className="card-dark p-3 mb-3 text-crema text-sm" style={{ whiteSpace: "pre-wrap" }} data-testid="admin-mensaje">{s.mensaje_admin}</div>
            <button onClick={() => copiarChequeo(s.mensaje_admin)} className="btn-dorado flex items-center gap-2" data-testid="admin-mensaje-copiar">
              {chequeoCopiado ? <Check size={14} /> : <Copy size={14} />} {chequeoCopiado ? "COPIADO" : "COPIAR ESTE MENSAJE"}
            </button>
          </div>
        )}

        {s.nota && (
          <div className="rounded-xl p-4 mb-7 text-gris text-sm" style={{ border: "1px solid #3C3C3C", background: "rgba(255,255,255,0.03)" }} data-testid="paso-nota">
            {s.nota}
          </div>
        )}

        {s.atajo && (
          <p className="text-gris text-xs mb-7" data-testid="paso-atajo">{s.atajo}</p>
        )}

        {s.criterio && (
          s.traer_respuesta ? (
            <div className="rounded-xl p-4 mb-7" style={{ border: pegado[pasoActual] ? "1px solid rgba(111,191,169,0.5)" : "1px solid rgba(201,168,76,0.5)", background: pegado[pasoActual] ? "rgba(111,191,169,0.06)" : "rgba(201,168,76,0.06)" }} data-testid="paso-criterio">
              <div className="mono-label text-dorado mb-2">PEGÁ ACÁ LA RESPUESTA QUE TE DIO {iaNombre.toUpperCase()}</div>
              <p className="text-gris text-xs mb-3">{s.criterio}</p>
              <textarea
                value={respuestaPegada}
                onChange={(e) => setRespuestaPegada(e.target.value)}
                placeholder={`Pegá acá lo que te devolvió ${iaNombre}…`}
                rows={4}
                className="w-full text-crema text-sm mb-3 resize-y focus:outline-none focus:border-dorado/60"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid #3C3C3C", borderRadius: "10px", padding: "12px" }}
                data-testid="respuesta-textarea"
              />
              <div className="flex items-center gap-3 flex-wrap">
                <button onClick={pegarRespuesta} className="btn-dorado flex items-center gap-2" data-testid="pegar-respuesta-btn">
                  <MessageCircle size={14} /> PEGAR LA RESPUESTA
                </button>
                {pegado[pasoActual] && (
                  <span className="text-verde text-xs flex items-center gap-1.5" data-testid="respuesta-recibida">
                    <Check size={13} /> Ya la reviso con vos acá abajo
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-xl p-4 mb-7" style={{ border: pegado[pasoActual] ? "1px solid rgba(111,191,169,0.5)" : "1px solid rgba(201,168,76,0.5)", background: pegado[pasoActual] ? "rgba(111,191,169,0.06)" : "rgba(201,168,76,0.06)" }} data-testid="paso-criterio">
              <p className="text-crema text-sm mb-3">{s.criterio}</p>
              <button onClick={() => setChatAbierto(true)} className="btn-fantasma flex items-center gap-2 text-xs" data-testid="criterio-pegar">
                <MessageCircle size={14} /> {pegado[pasoActual] ? "PEGADO — REVISALO ACÁ ABAJO" : "PEGÁMELO ACÁ ABAJO"}
              </button>
            </div>
          )
        )}

        {s.chequeo && !s.doc && (
          <div className="rounded-xl p-4 mb-7" style={{ border: "1px solid rgba(201,168,76,0.55)", background: "rgba(201,168,76,0.07)" }} data-testid="paso-chequeo">
            <div className="mono-label text-dorado mb-2">PROBÁ QUE QUEDÓ — PEGÁ ESTO EN {iaNombre.toUpperCase()}</div>
            <p className="text-crema text-sm mb-3" style={{ fontStyle: "italic" }}>"{s.chequeo}"</p>
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => copiarChequeo(s.chequeo)} className="btn-dorado flex items-center gap-2" data-testid="chequeo-copiar">
                {chequeoCopiado ? <Check size={14} /> : <Copy size={14} />} {chequeoCopiado ? "COPIADO" : "COPIAR LA PRUEBA"}
              </button>
              {s.chequeo_pista && <span className="text-gris text-xs">{s.chequeo_pista}</span>}
            </div>
          </div>
        )}

        {/* En el paso del documento: en mobile un botón abre el pedido a pantalla completa */}
        {splitStep && (
          <button onClick={() => setVerDoc(true)} className="btn-fantasma flex md:hidden items-center gap-2 mb-7 w-full justify-center" data-testid="ver-documento-btn">
            <FileText size={15} /> ABRIR EL PEDIDO PARA COPIAR
          </button>
        )}

        {impl.conector_ui && s.key === "f_probar" && <div className="space-y-4 mb-7 text-crema text-base" data-testid="guia-primera-prueba">
          <p>Volvé al mismo chat de ChatGPT. Adjuntá el archivo desde <b>+</b>, o escribí los datos que te pidió. Usá un ejemplo que puedas comprobar.</p>
          <p>Si querés, copiá este mensaje y envialo después de aportar los datos:</p>
          <MensajeParaCopiar texto={`Hacé una primera prueba de «${nombre}» con los datos que te pasé, siguiendo las instrucciones de Olimpo. Si falta algo, pedímelo antes de empezar. Mostrame el resultado para revisarlo. Esperá mi autorización antes de enviar, publicar, borrar o confirmar algo hacia afuera.`} testid="mensaje-prueba" />
          <p className="border-l-2 border-dorado pl-3"><b>Cuándo volver:</b> cuando ChatGPT te muestre el resultado, volvé a Olimpo y tocá YA TENGO EL RESULTADO, SEGUIR.</p>
        </div>}
        {impl.conector_ui && s.key === "corregir_probar" && <div className="space-y-3 mb-7 text-crema text-base" data-testid="guia-revisar">
          <ul className="list-disc pl-5 space-y-2"><li>¿Usó el archivo o los datos correctos?</li><li>¿El resultado coincide con lo que necesitás?</li><li>¿Quedó algo pendiente o algún error?</li></ul>
          <p>Si encontrás un error, explicáselo en el mismo chat y revisá la corrección. Avanzá cuando hayas terminado de revisar; en el último paso podés registrar que salió bien o qué falló.</p>
        </div>}
        {impl.conector_ui && s.key === "f_registrar" && !andando && <div className="space-y-4 mb-7 text-crema text-base" data-testid="guia-registrar">
          <p><b>1. En el mismo chat de ChatGPT:</b> contale cómo salió la prueba después de revisarla. Para guardar ese resultado, podés enviar este mensaje:</p>
          <MensajeParaCopiar texto={`Consultá en Olimpo el último resultado de «${nombre}» (ID ${id}). Si esta misma prueba ya está registrada, confirmalo sin volver a registrarla. Si no está registrada, registrá una sola vez cómo salió según lo que acabamos de revisar, con un resumen. Si no te confirmé el resultado o no estás seguro de si es la misma prueba, preguntame antes de guardar.`} etiqueta="COPIAR MENSAJE PARA REGISTRAR" testid="mensaje-registrar" />
          <p><b>2. Volvé a Olimpo:</b> tocá ACTUALIZAR RESULTADO y revisá el resumen de abajo. Si hubo un error al guardar, comprobá acá si llegó antes de enviar la misma prueba otra vez.</p>
          <p><b>3. Cuando se habilite:</b> tocá YA FUNCIONA, PONELO ANDANDO. {esSetter ? "Este proceso necesita tres pruebas seguidas correctas." : "Se habilita con una prueba registrada como correcta."}</p>
        </div>}
        {renderPasoControles(s)}
      </div>
    );
  }
}
